/**
 * Message Markdown
 *
 * Renders assistant and user message markdown with interactive affordances.
 *
 * Primary responsibilities:
 * - Render safe markdown, previews, annotations, and selection actions
 * - Track meaningful copy actions on assistant output selections
 *
 * Dependencies:
 * - Streamdown markdown renderer
 * - Chat message types
 */

/* eslint-disable tailwindcss/classnames-order */

import React, {
  FC,
  useCallback,
  useMemo,
  useState,
  useEffect,
  useRef,
  useId,
  ComponentPropsWithoutRef,
  type CSSProperties
} from 'react'
import { createPortal } from 'react-dom'
import {
  defaultRemarkPlugins,
  defaultUrlTransform,
  type AllowedTags,
  type StreamdownProps,
  type UrlTransform
} from 'streamdown'
import { useRouter, useParams, usePathname } from 'next/navigation'

import { CodeBlock } from '@/types/chat-message'
import { ExtendedMessage } from '@/types/messages'
import { getSafeMarkdownUrl, getSafeRenderableImageUrl } from '@/lib/media/safe-url'
import { trackMeaningfulOutcomeCompleted } from '@/lib/analytics/meaningful-outcome'
import { stripAssistantResponseControlMarkers } from '@/lib/utils/parse-suggestions'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle
} from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { FileIcon } from '@/components/ui/file-icon'
import { Input } from '@/components/ui/input'
import Loading from '@/components/ui/loading'
import { Progress } from '@/components/ui/progress'
import type { CodeViewer as CodeViewerComponent } from '@/components/code-viewer/code-viewer'

// CodeMirror and its language packs (~230 KB gz) load on the first code
// block instead of with every chat. Until then the code shows as plain text.
const LazyCodeViewer = React.lazy(() =>
  import('@/components/code-viewer/code-viewer').then((module) => ({
    default: module.CodeViewer
  }))
)

// --------------

function CodeViewer(props: React.ComponentProps<typeof CodeViewerComponent>) {
  return (
    <React.Suspense
      fallback={
        <pre className="bg-muted/40 my-3 overflow-x-auto whitespace-pre-wrap rounded-xl p-4 font-mono text-sm">
          {props.codeBlock?.code ?? ''}
        </pre>
      }
    >
      <LazyCodeViewer {...props} />
    </React.Suspense>
  )
}
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger
} from '@/components/ui/context-menu'
import {
  IconHighlight,
  IconMessageReply,
  IconPlus,
  IconMinus,
  IconArrowsMaximize,
  IconMaximize,
  IconMinimize,
  IconX,
  IconSearch,
  IconDownload,
  IconPresentation,
  IconChevronLeft,
  IconChevronRight,
  IconCopy,
  IconCheck
} from '@tabler/icons-react'
import { ImageWithPreview } from '../image/image-with-preview'
import { AnimatedMarkdownVideo } from '@/components/messages/animated-markdown-video'
import { AnimatedMarkdownMusic } from '@/components/messages/animated-markdown-music'
import { AnimatedMarkdown3D } from '@/components/messages/animated-markdown-3d'
import { MessageMarkdownMemoized } from './message-markdown-memoized'
import dynamic from 'next/dynamic'

// react-jsx-parser (~70 KB gz) loads only when a message contains a
// generated JSX preview. All three parts come from one shared chunk.
const JSXPreview = dynamic(
  () => import('@/components/ai-elements/jsx-preview').then((module) => module.JSXPreview),
  { loading: () => null }
)
const JSXPreviewContent = dynamic(
  () =>
    import('@/components/ai-elements/jsx-preview').then((module) => module.JSXPreviewContent),
  { loading: () => null }
)
const JSXPreviewError = dynamic(
  () => import('@/components/ai-elements/jsx-preview').then((module) => module.JSXPreviewError),
  { loading: () => null }
)
import {
  GeneratedActionButton,
  GeneratedActionPanel,
  GeneratedActionView,
  GeneratedCopyButton,
  GeneratedOpenButton,
  GeneratedPremiumButton,
  GeneratedPreviewActionsProvider,
  GeneratedStateButton,
  GeneratedToggleButton
} from '@/components/messages/generated-jsx-preview-actions'
import {
  HtmlCardHost,
  parseHtmlCardFence
} from '@/components/chat/html-card/HtmlCardHost'
import type { HtmlCardState } from '@/lib/chat/html-card/bridge'
import {
  isOpenUIFenceLanguage,
  OpenUIBlock
} from '@/components/messages/openui-block'
import { GetPremiumMarkdownButton } from '@/components/messages/get-premium-markdown-button'
import { streamdownAnimation } from '@/components/messages/streamdown-animation'
import { streamdownMathPlugins } from '@/components/messages/streamdown-math'
import type MermaidApi from 'mermaid'
import { TransformWrapper, TransformComponent, useControls } from 'react-zoom-pan-pinch'
import { Dialog, DialogContent } from '@/components/ui/dialog'
import { ExpandableInteractiveWrapper } from '@/components/ui/expandable-interactive-wrapper'
import { Flashcard, ConfidenceLevel } from '@/components/flashcard/flashcard'
import { FlashcardFocusMode, FlashcardItem } from '@/components/flashcard/flashcard-focus-mode'
import { FlashcardFocusButton } from '@/components/flashcard/flashcard-focus-button'
import { TodoList } from '@/components/todo/todo-list'
import { QuizQuestion } from '@/components/quiz/quiz-question'
import { QuizSession } from '@/components/quiz/quiz-session'
import { QuestionItem } from '@/components/quiz/question-focus-mode'
import { QuestionFocusButton } from '@/components/quiz/question-focus-button'
import {
  Timeline,
  CodeDiff,
  Poll,
  DefinitionCard,
  CodeSnippet,
  RatingCard,
  ProgressTracker,
  TabbedContent,
  Checklist,
  MultiStepForm,
  MindMap,
  ArgumentMap,
  FlowChart,
  GanttChart,
  DataDashboard,
  SystemMap,
  BusinessCanvas,
  CitationNetwork,
  StakeholderMap,
  ScenarioPlanner,
  MarketPositioner,
  VideoStoryboard,
  VideoTimestamp
} from '@/components/interactive'
import { CitationNetworkData } from '@/components/interactive/citation-network'
import { StakeholderMapData } from '@/components/interactive/stakeholder-map'
import { ScenarioPlannerData } from '@/components/interactive/scenario-planner'
import { MarketPositionerData } from '@/components/interactive/market-positioner'
import { safeConsole } from '@/lib/utils/safeConsole'
import { findPremiumCtaMarkers, replacePremiumCtaMarkers } from '@/lib/messages/premium-cta-tags'
import { repairBrokenMarkdownTables } from '@/lib/messages/repair-broken-markdown-tables'
import { motion } from 'framer-motion'
import { AnimatePresence } from 'framer-motion'
import { REGEX_NOTE } from '@/lib/preview'
import { RiStickyNoteLine } from 'react-icons/ri'
import {
  FlashcardSkeleton,
  QuizQuestionSkeleton,
  TodoListSkeleton,
  TimelineSkeleton,
  BusinessCanvasSkeleton,
  CitationNetworkSkeleton,
  StakeholderMapSkeleton,
  ScenarioPlannerSkeleton,
  MarketPositionerSkeleton,
  ChecklistSkeleton,
  MindMapSkeleton,
  FlowChartSkeleton,
  TabbedContentSkeleton,
  DefinitionCardSkeleton,
  PollSkeleton
} from '@/components/skeletons'
import { parseChecklistContent } from '@/lib/utils/checklist-parser'
import { TwitterEmbed } from './twitter-embed'
import { ExtensionConnectionBlock } from './extension-connection-block'
import { CrmMentionChip } from '@/components/chat/crm-mention-chip'
import { SpaceMemberMentionChip } from '@/components/chat/space-member-mention-chip'
import { CitationChip, getCitationNumber } from '@/components/messages/citation-chip'
import { ChatAppPreviewCard } from '@/components/apps/chat-app-preview-card'
import { SpaceItemMessageCard } from '@/components/messages/space-item-message-card'
import { LocationSharedCard } from '@/components/messages/location-shared-card'
import { TrendMessageCard } from '@/components/messages/trend-message-card'
import { YouTubeVisualizer } from '@/components/messages/youtube-visualizer'
import { useSplitPaneNavigation } from '@/components/splitview/split-pane-context'
import { toInAppHref } from '@/lib/navigation/internal-href'
import { getSpaceItemFromHref } from '@/lib/spaces/state'
import {
  buildSidekickSpaceItemMarkdownHref,
  dispatchSidekickOpenSpaceItem,
  getSidekickSpaceItemHref,
  parseSidekickSpaceItemCardPayload,
  parseSidekickSpaceItemCardPayloadLenient,
  parseSidekickSpaceItemMarkdownHref,
  parseSidekickSpaceItemTags,
  replaceSpaceItemCardMarkers,
  serializeSidekickSpaceItemCardTag,
  type SidekickSpaceItemContext
} from '@/lib/sidekick/space-item-context'
import {
  YOUTUBE_VIDEO_LIST_MARKER_REGEX,
  YOUTUBE_VIDEO_MARKER_REGEX,
  extractYoutubeVideoId,
  getYoutubeEmbedUrl,
  getYoutubeThumbnailUrl,
  parseYoutubeVideoListMarkerPayload,
  parseYoutubeVideoMarkerPayload,
  type YoutubeVideoMarker
} from '@/lib/youtube-markers'
import {
  LOSI_APP_PREVIEW_MARKER_REGEX,
  parseLosiAppPreviewMarkerPayload
} from '@/lib/apps/preview-marker'
import { LosiPlannerDayCard } from '@/components/messages/losi-planner-day-card'
import {
  LOSI_PLANNER_DAY_MARKER_REGEX,
  parseLosiPlannerDayMarkerPayload
} from '@/lib/planner-day-marker'
import { VisualArtifactRenderer } from '@/components/visual-artifacts/visual-artifact-renderer'
import {
  VISUAL_ARTIFACT_MARKER_REGEX,
  parseVisualArtifactMarkerPayload
} from '@/lib/visual-artifacts'
import { parseCitationSources } from '@/lib/citations'
import type { ChatHighlight, ChatHighlightDraft } from '@/lib/chat/chat-highlights'
import {
  MESSAGE_SELECTION_ACTIONS_ATTRIBUTE,
  MESSAGE_TEXT_SELECTION_ATTRIBUTE,
  MESSAGE_TEXT_SELECTION_CLEAR_EVENT,
  MESSAGE_TEXT_SELECTION_REPLACED_EVENT,
  dispatchMessageTextSelectionClear,
  dispatchMessageTextSelectionReplaced,
  getMessageTextSelectionElement,
  isMessageSelectionActionTarget,
  type MessageTextSelectionReplacedDetail
} from '@/lib/chat/message-text-selection'
import {
  getDomRangeOffsets,
  getTextContentForHighlight,
  unwrapTextHighlightMarks,
  wrapTextHighlight
} from '@/lib/chat/dom-text-highlights'

const YOUTUBE_URL_PATTERN = String.raw`https?:\/\/(?:(?:www\.|m\.|music\.)?youtube(?:-nocookie)?\.com|youtu\.be)\/[^\s<>)]+`
const YOUTUBE_MARKDOWN_LINK_REGEX = new RegExp(
  String.raw`\[([^\]\n]{1,160})\]\((${YOUTUBE_URL_PATTERN})\)`,
  'g'
)
const YOUTUBE_BARE_URL_REGEX = new RegExp(YOUTUBE_URL_PATTERN, 'g')
const TRAILING_YOUTUBE_URL_PUNCTUATION_REGEX = /[.,!?;:]+$/
const MARKDOWN_CODE_SEGMENT_REGEX = /```[\s\S]*?```|`[^`\n]*`/g
const MESSAGE_MARKDOWN_ALLOWED_TAGS = {
  'losi-app-preview': ['data-payload'],
  'losi-planner-day': ['data-payload'],
  'space-item-card': ['data-payload', 'data-loading'],
  'get-premium-button': ['data-payload']
} satisfies AllowedTags

type MessageMarkdownCustomTagProps = Record<string, unknown> & {
  children?: React.ReactNode
}

type InvalidVisualArtifactSummary = {
  title: string
  type: string
}

type SelectionActionPosition = {
  left: number
  top: number
  placement: 'above' | 'below'
}

type SelectionActionBubblesProps = {
  copied: boolean
  position: SelectionActionPosition
  onCopy: () => void
  onReply: () => void
  onHighlight: () => void
  onDismiss: () => void
}

type SafeSvgElementProps = Record<string, string | number | CSSProperties | undefined>

const protectMarkdownCodeSegments = (value: string) => {
  const segments: string[] = []
  const content = value.replace(MARKDOWN_CODE_SEGMENT_REGEX, (segment) => {
    const marker = `__YOUTUBE_CODE_SEGMENT_${segments.length}__`
    segments.push(segment)

    return marker
  })

  const restore = (nextValue: string) =>
    segments.reduce(
      (restored, segment, index) => restored.replace(`__YOUTUBE_CODE_SEGMENT_${index}__`, segment),
      nextValue
    )

  return { content, restore }
}

const getAutoYoutubeUrlParts = (value: string) => {
  const suffix = value.match(TRAILING_YOUTUBE_URL_PUNCTUATION_REGEX)?.[0] ?? ''

  return {
    url: suffix ? value.slice(0, -suffix.length) : value,
    suffix
  }
}

const getAutoYoutubeVideo = (rawUrl: string, rawTitle?: string): YoutubeVideoMarker | null => {
  const { url } = getAutoYoutubeUrlParts(rawUrl)
  const id = extractYoutubeVideoId(url)
  if (!id) return null

  const title = rawTitle?.replace(/\s+/g, ' ').trim() || 'YouTube video'

  return {
    id,
    url,
    title,
    embedUrl: getYoutubeEmbedUrl(id),
    thumbnailUrl: getYoutubeThumbnailUrl(id)
  }
}

const SELECTION_ACTION_MARGIN = 12
const SELECTION_ACTION_WIDTH = 284
const SELECTION_ACTION_VERTICAL_SPACE = 72
const HIGHLIGHT_CONTEXT_LENGTH = 80

const SAFE_SVG_TAGS = new Set([
  'a',
  'circle',
  'clipPath',
  'defs',
  'desc',
  'ellipse',
  'foreignObject',
  'g',
  'line',
  'linearGradient',
  'marker',
  'mask',
  'path',
  'pattern',
  'polygon',
  'polyline',
  'radialGradient',
  'rect',
  'stop',
  'style',
  'svg',
  'text',
  'textPath',
  'title',
  'tspan'
])

// Mermaid mindmaps / some flowchart labels still emit HTML inside foreignObject.
// Allow a tight HTML subset so node text shows without reopening XSS via scripts.
const SAFE_FOREIGN_OBJECT_HTML_TAGS = new Set([
  'a',
  'b',
  'br',
  'code',
  'div',
  'em',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'i',
  'img',
  'label',
  'li',
  'ol',
  'p',
  'pre',
  's',
  'small',
  'span',
  'strong',
  'sub',
  'sup',
  'table',
  'tbody',
  'td',
  'th',
  'thead',
  'tr',
  'u',
  'ul'
])

const SAFE_SVG_ATTRS = new Set([
  'alignmentBaseline',
  'aria-hidden',
  'aria-label',
  'className',
  'clipPath',
  'clipPathUnits',
  'cx',
  'cy',
  'd',
  'dominantBaseline',
  'dx',
  'dy',
  'fill',
  'fillOpacity',
  'fillRule',
  'filter',
  'fontFamily',
  'fontSize',
  'fontStyle',
  'fontWeight',
  'gradientTransform',
  'gradientUnits',
  'height',
  'href',
  'id',
  'markerEnd',
  'markerHeight',
  'markerStart',
  'markerUnits',
  'markerWidth',
  'mask',
  'offset',
  'opacity',
  'orient',
  'pathLength',
  'points',
  'preserveAspectRatio',
  'r',
  'refX',
  'refY',
  'requiredExtensions',
  'role',
  'rx',
  'ry',
  'spreadMethod',
  'src',
  'stopColor',
  'stopOpacity',
  'stroke',
  'strokeDasharray',
  'strokeDashoffset',
  'strokeLinecap',
  'strokeLinejoin',
  'strokeMiterlimit',
  'strokeOpacity',
  'strokeWidth',
  'style',
  'textAnchor',
  'transform',
  'viewBox',
  'width',
  'x',
  'x1',
  'x2',
  'xlinkHref',
  'xmlns',
  'y',
  'y1',
  'y2'
])

const SVG_ATTR_NAME_MAP: Record<string, string> = {
  class: 'className',
  'clip-path': 'clipPath',
  clipPathUnits: 'clipPathUnits',
  'dominant-baseline': 'dominantBaseline',
  'fill-opacity': 'fillOpacity',
  'fill-rule': 'fillRule',
  'font-family': 'fontFamily',
  'font-size': 'fontSize',
  'font-style': 'fontStyle',
  'font-weight': 'fontWeight',
  'gradient-transform': 'gradientTransform',
  'gradient-units': 'gradientUnits',
  'marker-end': 'markerEnd',
  'marker-height': 'markerHeight',
  'marker-start': 'markerStart',
  'marker-units': 'markerUnits',
  'marker-width': 'markerWidth',
  'path-length': 'pathLength',
  'preserve-aspect-ratio': 'preserveAspectRatio',
  refX: 'refX',
  refY: 'refY',
  'required-extensions': 'requiredExtensions',
  'stop-color': 'stopColor',
  'stop-opacity': 'stopOpacity',
  'stroke-dasharray': 'strokeDasharray',
  'stroke-dashoffset': 'strokeDashoffset',
  'stroke-linecap': 'strokeLinecap',
  'stroke-linejoin': 'strokeLinejoin',
  'stroke-miterlimit': 'strokeMiterlimit',
  'stroke-opacity': 'strokeOpacity',
  'stroke-width': 'strokeWidth',
  'text-anchor': 'textAnchor',
  'xlink:href': 'xlinkHref'
}

function readMessageMarkdownPayload(props: Record<string, unknown>) {
  const payload = props['data-payload']
  if (typeof payload !== 'string') return ''

  try {
    return decodeURIComponent(payload)
  } catch {
    return payload
  }
}

function getInvalidVisualArtifactSummary(payload: string): InvalidVisualArtifactSummary {
  try {
    const parsed = JSON.parse(payload) as unknown

    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return { title: 'Visual artifact', type: 'unsupported payload' }
    }

    const record = parsed as Record<string, unknown>
    const title =
      typeof record.title === 'string' && record.title.trim()
        ? record.title.trim()
        : 'Visual artifact'
    const type =
      typeof record.type === 'string' && record.type.trim()
        ? record.type.trim()
        : 'unsupported type'

    return { title, type }
  } catch {
    return { title: 'Visual artifact', type: 'invalid JSON' }
  }
}

function InvalidVisualArtifactFallback({
  payload
}: {
  payload: string
}) {
  const summary = getInvalidVisualArtifactSummary(payload)

  return (
    <section
      aria-label={`${summary.title} could not be rendered`}
      className="not-prose my-4 rounded-lg border border-amber-500/30 bg-amber-500/5 p-3"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="text-sm font-medium text-foreground">{summary.title}</div>
          <div className="text-muted-foreground text-xs">{summary.type}</div>
        </div>
        <span className="rounded-full border border-amber-500/30 px-2 py-0.5 text-[11px] font-medium text-amber-700 dark:text-amber-300">
          Artifact needs valid data
        </span>
      </div>
      <p className="text-muted-foreground mt-2 text-xs leading-5">
        This visual artifact was hidden because its payload does not match the supported
        schema. Regenerate it with supported 3D formats, map coordinates, route data, or
        blueprint element kinds.
      </p>
    </section>
  )
}

function SafeSvgMarkup({ className, svg }: { className?: string; svg: string }) {
  const node = useMemo(() => parseSafeSvgMarkup(svg), [svg])

  if (!node) {
    return null
  }

  return <div className={className}>{node}</div>
}

/**
 * Mermaid SVGs often put labels in <foreignObject><div>…</div></foreignObject>.
 * Rebuilding that tree with React.createElement drops/mis-namespaces the HTML,
 * so nodes paint empty. Import the sanitized SVG node instead.
 */
function MermaidSvgHost({ className, svg }: { className?: string; svg: string }) {
  const hostRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const host = hostRef.current
    if (!host) return

    host.replaceChildren()

    const sanitized = sanitizeMermaidSvgMarkup(svg)
    if (!sanitized) return

    const parsed = new DOMParser().parseFromString(sanitized, 'image/svg+xml')
    if (parsed.querySelector('parsererror')) return

    const root = parsed.documentElement
    if (!(root instanceof SVGSVGElement) && root.tagName.toLowerCase() !== 'svg') {
      return
    }

    host.appendChild(document.importNode(root, true))
  }, [svg])

  return <div ref={hostRef} className={className} />
}

function sanitizeMermaidSvgMarkup(svg: string) {
  const trimmed = svg.trim()
  if (!trimmed.startsWith('<svg')) return null

  const parsed = new DOMParser().parseFromString(trimmed, 'image/svg+xml')
  if (parsed.querySelector('parsererror')) return null

  const walk = (node: Element) => {
    // Drop executable / navigational payloads Mermaid never needs.
    if (/^(script|iframe|object|embed|link|meta)$/i.test(node.tagName)) {
      node.remove()
      return
    }

    for (const attribute of Array.from(node.attributes)) {
      const name = attribute.name.toLowerCase()
      const value = attribute.value.trim()

      if (name.startsWith('on') || name === 'srcdoc') {
        node.removeAttribute(attribute.name)
        continue
      }

      if (
        (name === 'href' || name === 'xlink:href' || name === 'src') &&
        /^javascript:/i.test(value)
      ) {
        node.removeAttribute(attribute.name)
      }
    }

    for (const child of Array.from(node.children)) {
      walk(child)
    }
  }

  walk(parsed.documentElement)
  return new XMLSerializer().serializeToString(parsed.documentElement)
}

function parseSafeSvgMarkup(svg: string) {
  if (typeof window === 'undefined' || !svg.trim().startsWith('<svg')) {
    return null
  }

  const document = new DOMParser().parseFromString(svg, 'image/svg+xml')

  if (document.querySelector('parsererror')) {
    return null
  }

  return renderSafeSvgNode(document.documentElement, 'svg-root', false)
}

function renderSafeSvgNode(
  node: Node,
  key: string,
  insideForeignObject: boolean
): React.ReactNode {
  if (node.nodeType === Node.TEXT_NODE) {
    return node.textContent
  }

  if (!(node instanceof Element)) {
    return null
  }

  const rawTag = normalizeSvgTagName(node.tagName)
  const lowerTag = rawTag.toLowerCase()
  const isForeignObject = lowerTag === 'foreignobject'
  const reactTag = isForeignObject
    ? 'foreignObject'
    : insideForeignObject
      ? lowerTag
      : rawTag

  const allowed =
    SAFE_SVG_TAGS.has(isForeignObject ? 'foreignObject' : rawTag) ||
    (insideForeignObject && SAFE_FOREIGN_OBJECT_HTML_TAGS.has(lowerTag))

  if (!allowed) {
    return null
  }

  if (reactTag === 'style') {
    return <style key={key}>{sanitizeSvgStyleText(node.textContent ?? '')}</style>
  }

  const props = readSafeSvgProps(node, insideForeignObject || isForeignObject)
  const nextInsideForeignObject = insideForeignObject || isForeignObject
  const children = Array.from(node.childNodes).map((child, index) =>
    renderSafeSvgNode(child, `${key}-${index}`, nextInsideForeignObject)
  )

  return React.createElement(reactTag, { ...props, key }, children)
}

function normalizeSvgTagName(tagName: string) {
  const value = tagName.includes(':') ? (tagName.split(':').pop() ?? tagName) : tagName
  if (value.toLowerCase() === 'clippath') return 'clipPath'
  if (value.toLowerCase() === 'lineargradient') return 'linearGradient'
  if (value.toLowerCase() === 'radialgradient') return 'radialGradient'
  if (value.toLowerCase() === 'textpath') return 'textPath'
  return value
}

function readSafeSvgProps(element: Element, allowHtmlAttrs = false) {
  return Array.from(element.attributes).reduce<SafeSvgElementProps>((props, attribute) => {
    const name = SVG_ATTR_NAME_MAP[attribute.name] ?? attribute.name
    const value = attribute.value

    if (name.toLowerCase().startsWith('on') || name === 'srcdoc') {
      return props
    }

    if (!SAFE_SVG_ATTRS.has(name) && !(allowHtmlAttrs && name === 'className')) {
      return props
    }

    if ((name === 'href' || name === 'xlinkHref') && isUnsafeSvgUrl(value)) {
      return props
    }

    if (name === 'src' && isUnsafeForeignObjectImageSrc(value)) {
      return props
    }

    if (name === 'style') {
      const style = parseSafeSvgStyle(value)
      if (style) {
        props.style = style
      }
      return props
    }

    props[name] = value
    return props
  }, {})
}

function parseSafeSvgStyle(value: string): CSSProperties | null {
  const entries = value
    .split(';')
    .map((part) => part.trim())
    .filter(Boolean)
    .flatMap((part) => {
      const separator = part.indexOf(':')
      if (separator === -1) return []

      const key = part.slice(0, separator).trim()
      const styleValue = part.slice(separator + 1).trim()
      if (!key || !styleValue || isUnsafeSvgStyleValue(styleValue)) return []

      return [[kebabToCamel(key), styleValue] as const]
    })

  return entries.length > 0 ? (Object.fromEntries(entries) as CSSProperties) : null
}

function sanitizeSvgStyleText(value: string) {
  return value
    .replace(/@import[^;]+;?/gi, '')
    .replace(/url\((?!#)[^)]+\)/gi, '')
    .replace(/javascript:/gi, '')
}

function isUnsafeSvgStyleValue(value: string) {
  return /javascript:|expression\(|url\((?!#)/i.test(value)
}

function isUnsafeSvgUrl(value: string) {
  const trimmed = value.trim().toLowerCase()
  return Boolean(trimmed && !trimmed.startsWith('#') && !trimmed.startsWith('data:image/'))
}

function isUnsafeForeignObjectImageSrc(value: string) {
  const trimmed = value.trim().toLowerCase()
  if (!trimmed) return true
  if (trimmed.startsWith('data:image/')) return false
  if (/^https?:\/\//i.test(trimmed)) return false
  return true
}

function kebabToCamel(value: string) {
  return value.replace(/-([a-z])/g, (_, letter: string) => letter.toUpperCase())
}

function createSvgStop(offset: string, stopColor: string) {
  const stop = document.createElementNS('http://www.w3.org/2000/svg', 'stop')
  stop.setAttribute('offset', offset)
  stop.setAttribute('stop-color', stopColor)
  return stop
}

function getSelectionRect(range: Range) {
  const rects = Array.from(range.getClientRects()).filter(
    (rect) => rect.width > 0 && rect.height > 0
  )
  if (rects[0]) return rects[0]

  return range.getBoundingClientRect()
}

function getSelectionActionPosition(rect: DOMRect): SelectionActionPosition {
  const actionWidth = Math.min(
    SELECTION_ACTION_WIDTH,
    Math.max(1, window.innerWidth - SELECTION_ACTION_MARGIN * 2)
  )
  const minLeft = SELECTION_ACTION_MARGIN + actionWidth / 2
  const maxLeft = window.innerWidth - SELECTION_ACTION_MARGIN - actionWidth / 2
  const safeLeft = maxLeft < minLeft ? window.innerWidth / 2 : null
  const centerLeft = rect.left + rect.width / 2
  const left = safeLeft ?? Math.min(Math.max(centerLeft, minLeft), maxLeft)
  const hasAboveSpace = rect.top > SELECTION_ACTION_VERTICAL_SPACE

  return {
    left,
    top: Math.max(SELECTION_ACTION_MARGIN, hasAboveSpace ? rect.top - 10 : rect.bottom + 10),
    placement: hasAboveSpace ? 'above' : 'below'
  }
}

function getSelectionOffsets(container: HTMLElement, range: Range, selectedText: string) {
  const textNodeOffsets = getDomRangeOffsets(container, range, selectedText)

  if (textNodeOffsets) {
    const fullText = getTextContentForHighlight(container)

    return {
      ...textNodeOffsets,
      prefix: fullText.slice(
        Math.max(0, textNodeOffsets.startOffset - HIGHLIGHT_CONTEXT_LENGTH),
        textNodeOffsets.startOffset
      ),
      suffix: fullText.slice(
        textNodeOffsets.endOffset,
        textNodeOffsets.endOffset + HIGHLIGHT_CONTEXT_LENGTH
      )
    }
  }

  const preSelectionRange = range.cloneRange()
  preSelectionRange.selectNodeContents(container)
  preSelectionRange.setEnd(range.startContainer, range.startOffset)

  const fullSelectionRange = document.createRange()
  fullSelectionRange.selectNodeContents(container)
  const fullText = fullSelectionRange.toString()
  const rawSelectionText = range.toString()
  const trimmedStart = Math.max(0, rawSelectionText.indexOf(selectedText))
  const startOffset = preSelectionRange.toString().length + trimmedStart
  const endOffset = startOffset + selectedText.length

  return {
    startOffset,
    endOffset,
    prefix: fullText.slice(Math.max(0, startOffset - HIGHLIGHT_CONTEXT_LENGTH), startOffset),
    suffix: fullText.slice(endOffset, endOffset + HIGHLIGHT_CONTEXT_LENGTH)
  }
}

async function copyPlainText(text: string) {
  const selection = window.getSelection()
  const selectedRanges = selection
    ? Array.from({ length: selection.rangeCount }, (_, index) =>
        selection.getRangeAt(index).cloneRange()
      )
    : []

  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text)
      return true
    }
  } catch {
    // Fall back to the textarea copy path below.
  }

  const textarea = document.createElement('textarea')
  textarea.value = text
  textarea.setAttribute('readonly', '')
  textarea.style.position = 'fixed'
  textarea.style.left = '-9999px'
  document.body.appendChild(textarea)
  textarea.select()

  try {
    return document.execCommand('copy')
  } finally {
    document.body.removeChild(textarea)
    if (selection && selectedRanges.length > 0) {
      selection.removeAllRanges()
      selectedRanges.forEach((range) => selection.addRange(range))
    }
  }
}

function SelectionActionBubbles({
  copied,
  position,
  onCopy,
  onReply,
  onHighlight,
  onDismiss
}: SelectionActionBubblesProps) {
  if (typeof document === 'undefined') return null

  return createPortal(
    <div
      data-selection-actions="true"
      {...{ [MESSAGE_SELECTION_ACTIONS_ATTRIBUTE]: 'true' }}
      role="toolbar"
      aria-label="Selected text actions"
      aria-orientation="horizontal"
      onMouseDown={(event) => {
        event.preventDefault()
        event.stopPropagation()
      }}
      onPointerDown={(event) => {
        event.preventDefault()
        event.stopPropagation()
      }}
      onKeyDown={(event) => {
        if (event.key !== 'Escape') return
        event.preventDefault()
        onDismiss()
      }}
      className="fixed z-[9999] inline-flex max-w-[calc(100vw-24px)] touch-manipulation overflow-x-auto rounded-full border bg-popover/95 text-popover-foreground shadow-xl shadow-black/10 backdrop-blur-md dark:shadow-black/30"
      style={{
        left: position.left,
        top: position.top,
        transform: position.placement === 'above' ? 'translate(-50%, -100%)' : 'translate(-50%, 0)'
      }}
    >
      <button
        type="button"
        aria-label={copied ? 'Copied selected text' : 'Copy selected text'}
        onClick={onCopy}
        className="inline-flex h-9 min-w-[78px] shrink-0 items-center justify-center gap-1.5 px-3 text-sm font-medium transition-colors hover:bg-accent focus-visible:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary/40"
      >
        {copied ? (
          <IconCheck aria-hidden="true" size={16} />
        ) : (
          <IconCopy aria-hidden="true" size={16} />
        )}
        <span aria-live="polite">{copied ? 'Copied' : 'Copy'}</span>
      </button>
      <span aria-hidden="true" className="my-2 w-px shrink-0 bg-border" />
      <button
        type="button"
        aria-label="Reply to selected text"
        onClick={onReply}
        className="inline-flex h-9 min-w-[78px] shrink-0 items-center justify-center gap-1.5 px-3 text-sm font-medium text-primary transition-colors hover:bg-primary/10 focus-visible:bg-primary/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary/40"
      >
        <IconMessageReply aria-hidden="true" size={16} />
        <span>Reply</span>
      </button>
      <span aria-hidden="true" className="my-2 w-px shrink-0 bg-border" />
      <button
        type="button"
        aria-label="Highlight selected text"
        onClick={onHighlight}
        className="inline-flex h-9 min-w-[102px] shrink-0 items-center justify-center gap-1.5 px-3 text-sm font-medium text-amber-700 transition-colors hover:bg-amber-500/10 focus-visible:bg-amber-500/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary/40 dark:text-amber-300"
      >
        <IconHighlight aria-hidden="true" size={16} />
        <span>Highlight</span>
      </button>
    </div>,
    document.body
  )
}

// Explicitly define the QuizItem interface to match the one expected by QuizSession
interface QuizItem {
  question: string
  options: string[]
  correctAnswer: string
  explanation: string
  conceptExplanation?: string
  feedback?: string
  categoryTag?: string
  difficultyLevel?: 'easy' | 'medium' | 'hard'
}

// Initialize mermaid for client-side
let mermaidInitialized = false
let mermaidThemeMode: MessageMermaidThemeMode | null = null

type MessageMermaidThemeMode = 'dark' | 'light'

function getMessageMermaidThemeMode(): MessageMermaidThemeMode {
  return document.documentElement.classList.contains('dark') ? 'dark' : 'light'
}

function getMessageMermaidThemeVariables(themeMode: MessageMermaidThemeMode) {
  const isDark = themeMode === 'dark'
  const textColor = isDark ? '#e2e8f0' : '#1e293b'
  const mutedTextColor = isDark ? '#cbd5e1' : '#334155'
  const lineColor = isDark ? '#94a3b8' : '#64748b'
  const borderColor = isDark ? '#475569' : '#cbd5e1'

  return {
    darkMode: isDark,
    background: 'transparent',
    mainBkg: 'transparent',
    primaryColor: '#3b82f6',
    primaryTextColor: textColor,
    primaryBorderColor: '#3b82f6',
    lineColor,
    secondaryColor: '#7c3aed',
    tertiaryColor: '#06b6d4',
    nodeBorder: '2px',
    clusterBorder: '2px',
    defaultLinkColor: '#6366f1',
    titleColor: isDark ? '#f1f5f9' : '#0f172a',
    edgeLabelBackground: isDark ? '#1e293b80' : '#f8fafc80',
    fontSize: '16px',
    labelTextColor: mutedTextColor,
    nodeTextColor: textColor,
    mainBorderColor: borderColor,
    clusterBkg: isDark ? '#1e293b40' : '#f8fafc40',
    clusterTextColor: textColor,
    xyChart: {
      backgroundColor: 'transparent',
      titleColor: textColor,
      xAxisTitleColor: textColor,
      xAxisLabelColor: mutedTextColor,
      xAxisTickColor: lineColor,
      xAxisLineColor: lineColor,
      yAxisTitleColor: textColor,
      yAxisLabelColor: mutedTextColor,
      yAxisTickColor: lineColor,
      yAxisLineColor: lineColor,
      plotColorPalette: isDark
        ? '#60a5fa,#facc15,#34d399,#f87171,#a78bfa,#22d3ee'
        : '#2563eb,#d97706,#059669,#dc2626,#7c3aed,#0891b2'
    }
  }
}

let mermaidLoader: Promise<typeof MermaidApi> | null = null

// Load mermaid on first diagram render instead of shipping it with every
// chat bundle. Applies the same global config the eager init used to.
function loadMermaid(): Promise<typeof MermaidApi> {
  if (!mermaidLoader) {
    mermaidLoader = import('mermaid')
      .then(({ default: mermaid }) => {
        initializeMermaidGlobally(mermaid)
        return mermaid
      })
      .catch((error) => {
        // Allow a retry on the next render if the chunk failed to load
        mermaidLoader = null
        throw error
      })
  }
  return mermaidLoader
}

// --------------

function initializeMermaidGlobally(mermaid: typeof MermaidApi) {
  // console.log('[Mermaid] Initializing global Mermaid instance');
  try {
    const themeMode = getMessageMermaidThemeMode()

    mermaid.initialize({
      startOnLoad: true,
      theme: themeMode === 'dark' ? 'dark' : 'default',
      securityLevel: 'loose',
      logLevel: process.env.NODE_ENV === 'development' ? 'debug' : 'error',
      fontFamily: 'Inter, system-ui, -apple-system, sans-serif',
      maxTextSize: 50000, // Increased text size limit
      deterministicIds: true, // For consistent animations
      themeVariables: getMessageMermaidThemeVariables(themeMode),
      flowchart: {
        // Prefer SVG text labels; foreignObject HTML labels are still allowed
        // through SafeSvgMarkup's tight HTML subset (mindmaps need them).
        htmlLabels: false,
        curve: 'basis',
        padding: 20,
        useMaxWidth: true,
        diagramPadding: 8,
        nodeSpacing: 50,
        rankSpacing: 50,
        arrowMarkerAbsolute: true
      },
      sequence: {
        useMaxWidth: true,
        showSequenceNumbers: true,
        actorMargin: 80,
        messageMargin: 40,
        messageFontWeight: 400,
        noteFontWeight: 400,
        messageAlign: 'center',
        mirrorActors: false,
        bottomMarginAdj: 10,
        rightAngles: false,
        wrap: true,
        wrapPadding: 10
      },
      gantt: {
        useMaxWidth: true,
        topPadding: 50,
        leftPadding: 75,
        gridLineStartPadding: 35,
        barHeight: 50,
        barGap: 4,
        topAxis: false,
        axisFormat: '%m',
        tickInterval: '1month'
      },
      pie: {
        useMaxWidth: true,
        textPosition: 0.75
      },
      mindmap: {
        useMaxWidth: true,
        padding: 10,
        maxNodeWidth: 200
      },
      timeline: {
        useMaxWidth: true,
        padding: 20
      },
      gitGraph: {
        useMaxWidth: true,
        showBranches: true,
        showCommitLabel: true,
        mainBranchName: 'main',
        rotateCommitLabel: true
      },
      xyChart: {
        width: 900,
        height: 500,
        titleFontSize: 24,
        showTitle: true,
        titlePadding: 30,
        xAxis: {
          showLabel: true,
          labelFontSize: 16,
          labelPadding: 15,
          showTitle: true,
          titleFontSize: 18,
          titlePadding: 20,
          showTick: true,
          tickLength: 10,
          tickWidth: 2,
          showAxisLine: true,
          axisLineWidth: 2
        },
        yAxis: {
          showLabel: true,
          labelFontSize: 16,
          labelPadding: 15,
          showTitle: true,
          titleFontSize: 18,
          titlePadding: 20,
          showTick: true,
          tickLength: 10,
          tickWidth: 2,
          showAxisLine: true,
          axisLineWidth: 2
        }
      },
      sankey: {
        useMaxWidth: true,
        width: 900,
        height: 500,
        linkColor: 'gradient',
        nodeAlignment: 'justify',
        showValues: true
      },
      requirement: {
        useMaxWidth: true,
        rect_fill: document.documentElement.classList.contains('dark') ? '#1e293b' : '#f8fafc',
        text_color: document.documentElement.classList.contains('dark') ? '#e2e8f0' : '#1e293b',
        rect_border_size: '1px',
        rect_border_color: document.documentElement.classList.contains('dark')
          ? '#475569'
          : '#cbd5e1',
        rect_min_width: 150,
        rect_min_height: 50,
        fontSize: 14,
        rect_padding: 10,
        line_height: 1.2
      },
      c4: {
        useMaxWidth: true,
        diagramMarginX: 50,
        diagramMarginY: 50,
        c4ShapeMargin: 30,
        c4ShapePadding: 20,
        width: 200,
        height: 120,
        boxMargin: 10,
        c4ShapeInRow: 4,
        nextLinePaddingX: 30,
        c4BoundaryInRow: 2,
        personFontSize: 14,
        personFontWeight: 'normal',
        systemFontSize: 14,
        systemFontWeight: 'normal',
        messageFontSize: 12,
        messageFontWeight: 'normal'
      }
    })
    mermaidInitialized = true
    mermaidThemeMode = themeMode

    // console.log('[Mermaid] Global initialization successful');
  } catch (error) {
    // console.error('[Mermaid] Global initialization failed:', error);
    mermaidInitialized = false
    mermaidThemeMode = null
  }
}

const ACTIVE_STREAMING_MARKDOWN_PROPS = {
  mode: 'streaming',
  animated: {
    ...streamdownAnimation,
    stagger: 0
  },
  parseIncompleteMarkdown: true,
  isAnimating: true
} satisfies Pick<StreamdownProps, 'animated' | 'isAnimating' | 'mode' | 'parseIncompleteMarkdown'>

/**
 * Tailwind Typography paints `pre` with near-white `--tw-prose-pre-code`
 * (meant for dark shells). Streamdown code cards use light `bg-background` /
 * `bg-sidebar`, so unhighlighted tokens inherit white-on-white. Force
 * foreground on Streamdown code shells; Shiki `--sdm-c` still wins when set.
 */
const MESSAGE_MARKDOWN_PROSE_CLASSNAME =
  'prose min-h-[40px] min-w-0 w-full max-w-full space-y-6 break-words dark:prose-invert prose-p:leading-relaxed prose-pre:p-0 prose-pre:max-w-full prose-pre:text-foreground [&_[data-streamdown=code-block]]:text-foreground [&_[data-streamdown=code-block-body]]:text-foreground [&_[data-streamdown=table-wrapper]]:w-full [&_table]:w-full [&_table]:max-w-full [&_th]:align-top [&_td]:align-top [&_td]:min-w-0 [&_td]:overflow-hidden [&_td_space-item-card]:block [&_td_space-item-card]:max-w-full'

type MarkdownRemarkPlugins = NonNullable<StreamdownProps['remarkPlugins']>

const MESSAGE_MARKDOWN_REMARK_PLUGINS = [
  ...Object.values(defaultRemarkPlugins)
] satisfies MarkdownRemarkPlugins

interface MessageMarkdownProps {
  isGenerating?: boolean
  animateOnOpen?: boolean
  content: string
  codeBlocks?: CodeBlock[]
  onSelectCodeBlock?: (codeBlock: CodeBlock | null) => void
  experimental_code_editor?: boolean
  messageId?: string
  onSelectMessage?: (message: ExtendedMessage | null) => void
  message?: ExtendedMessage
  highlights?: ChatHighlight[]
  onCreateHighlight?: (highlight: ChatHighlightDraft) => void | Promise<void>
  /** When scrolling from quote click, highlight this text in the rendered content */
  highlightQuoteText?: string
  /** HTML-card LosiCard.submit(...) → send as a user chat reply */
  onInteractiveSubmit?: (payload: string) => void
}

const JSX_PREVIEW_LANGUAGES = new Set([
  'jsx-preview',
  'jsxpreview',
  'generative-ui',
  'ui-preview',
  'preview',
  'ui'
])

const HTML_CARD_LANGUAGES = new Set([
  'html-card',
  'htmlcard',
  'losi-card',
  'interactive-card'
])

type TodoPreviewProps = {
  className?: string
  items?: string[]
}

const TodoPreview = ({ className, items = [] }: TodoPreviewProps) => (
  <TodoList className={className} items={items} />
)

function createPreviewHtmlElement<Tag extends keyof React.JSX.IntrinsicElements>(tag: Tag) {
  const PreviewHtmlElement = (props: React.JSX.IntrinsicElements[Tag]) =>
    React.createElement(tag, props)

  return PreviewHtmlElement
}

const JSX_PREVIEW_HTML_COMPONENTS = {
  article: createPreviewHtmlElement('article'),
  aside: createPreviewHtmlElement('aside'),
  blockquote: createPreviewHtmlElement('blockquote'),
  br: createPreviewHtmlElement('br'),
  code: createPreviewHtmlElement('code'),
  dd: createPreviewHtmlElement('dd'),
  div: createPreviewHtmlElement('div'),
  dl: createPreviewHtmlElement('dl'),
  dt: createPreviewHtmlElement('dt'),
  em: createPreviewHtmlElement('em'),
  footer: createPreviewHtmlElement('footer'),
  h1: createPreviewHtmlElement('h1'),
  h2: createPreviewHtmlElement('h2'),
  h3: createPreviewHtmlElement('h3'),
  h4: createPreviewHtmlElement('h4'),
  header: createPreviewHtmlElement('header'),
  hr: createPreviewHtmlElement('hr'),
  label: createPreviewHtmlElement('label'),
  li: createPreviewHtmlElement('li'),
  main: createPreviewHtmlElement('main'),
  nav: createPreviewHtmlElement('nav'),
  ol: createPreviewHtmlElement('ol'),
  p: createPreviewHtmlElement('p'),
  pre: createPreviewHtmlElement('pre'),
  section: createPreviewHtmlElement('section'),
  small: createPreviewHtmlElement('small'),
  span: createPreviewHtmlElement('span'),
  strong: createPreviewHtmlElement('strong'),
  table: createPreviewHtmlElement('table'),
  tbody: createPreviewHtmlElement('tbody'),
  td: createPreviewHtmlElement('td'),
  th: createPreviewHtmlElement('th'),
  thead: createPreviewHtmlElement('thead'),
  tr: createPreviewHtmlElement('tr'),
  ul: createPreviewHtmlElement('ul')
}

const JSX_PREVIEW_COMPONENTS = {
  ...JSX_PREVIEW_HTML_COMPONENTS,
  ActionButton: GeneratedActionButton,
  ActionPanel: GeneratedActionPanel,
  ActionView: GeneratedActionView,
  Badge,
  Button: GeneratedActionButton,
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
  Checkbox,
  CopyButton: GeneratedCopyButton,
  Input,
  OpenButton: GeneratedOpenButton,
  PaywallButton: GeneratedPremiumButton,
  PremiumButton: GeneratedPremiumButton,
  Progress,
  GetPremiumButton: GeneratedPremiumButton,
  StateButton: GeneratedStateButton,
  TodoList: TodoPreview,
  ToggleButton: GeneratedToggleButton
}

function parseStringArrayLiteral(value: string): string[] | null {
  const items: string[] = []
  const itemRegex = /(["'])(.*?)\1/g
  let match: RegExpExecArray | null

  while ((match = itemRegex.exec(value)) !== null) {
    items.push(match[2].replace(/\\(["'])/g, '$1'))
  }

  return items.length > 0 ? items : null
}

function extractJsxPreviewBindings(code: string) {
  const bindings: Record<string, unknown> = {}
  const codeWithoutBindings = code.replace(
    /const\s+([A-Za-z_$][\w$]*)\s*=\s*(\[[\s\S]*?\])\s*;?/g,
    (match, name: string, arrayLiteral: string) => {
      const values = parseStringArrayLiteral(arrayLiteral)
      if (!values) return match

      bindings[name] = values
      return ''
    }
  )

  return {
    bindings,
    code: codeWithoutBindings
  }
}

function extractReturnedJsx(code: string) {
  const returnIndex = code.indexOf('return')
  if (returnIndex === -1) return null

  const parenStart = code.indexOf('(', returnIndex)
  if (parenStart === -1) {
    const inlineReturn = code.slice(returnIndex + 'return'.length).trim()
    const semicolonIndex = inlineReturn.lastIndexOf(';')
    return (semicolonIndex === -1 ? inlineReturn : inlineReturn.slice(0, semicolonIndex)).trim()
  }

  let depth = 0
  for (let index = parenStart; index < code.length; index += 1) {
    const char = code[index]
    if (char === '(') depth += 1
    if (char === ')') depth -= 1

    if (depth === 0) {
      return code.slice(parenStart + 1, index).trim()
    }
  }

  return null
}

function looksLikeJsxComponentMarkup(code: string) {
  return /<[A-Z][A-Za-z0-9.]*(?:\s|\/|>)/.test(code)
}

function getJsxPreviewData(code: string, language: string) {
  const normalizedLanguage = language.trim().toLowerCase()
  const isDedicatedPreview = JSX_PREVIEW_LANGUAGES.has(normalizedLanguage)
  const isImplicitJsxFence =
    (normalizedLanguage === 'html' ||
      normalizedLanguage === 'htm' ||
      normalizedLanguage === 'jsx' ||
      normalizedLanguage === 'tsx' ||
      normalizedLanguage === 'javascript' ||
      normalizedLanguage === 'js' ||
      normalizedLanguage === 'typescript' ||
      normalizedLanguage === 'ts') &&
    looksLikeJsxComponentMarkup(code)

  if (!isDedicatedPreview && !isImplicitJsxFence) {
    return null
  }

  const withoutImports = code
    .replace(/^\s*import\s+.*$/gm, '')
    .replace(/^\s*export\s+default\s+/gm, '')
    .trim()
  const extracted = extractJsxPreviewBindings(withoutImports)
  const returnedJsx = extractReturnedJsx(extracted.code)
  const jsx = (returnedJsx ?? extracted.code).trim()

  if (!jsx.startsWith('<')) {
    return null
  }

  return {
    bindings: extracted.bindings,
    jsx
  }
}

function shouldRenderHtmlCardFence(language: string, code: string) {
  const normalizedLanguage = language.trim().toLowerCase()
  if (!code.trim()) return false
  if (HTML_CARD_LANGUAGES.has(normalizedLanguage)) return true
  // Plain ```html fences with markup should render, not dump as a code file.
  // Skip when the body is clearly JSX component trees (those use jsx-preview).
  if (
    (normalizedLanguage === 'html' || normalizedLanguage === 'htm') &&
    code.trim().startsWith('<') &&
    !looksLikeJsxComponentMarkup(code)
  ) {
    return true
  }
  return false
}

function renderHtmlCardFromFence(
  code: string,
  language: string,
  keyHint: string,
  onSubmit?: (payload: string) => void,
  options?: {
    /** While streaming, the fence grows token-by-token; mounting the
        iframe then reloads it on every chunk (state reset + flicker). */
    isStreaming?: boolean
    states?: Record<string, HtmlCardState>
    onStateChange?: (cardId: string, state: HtmlCardState) => void
  }
) {
  const normalizedLanguage = language.trim().toLowerCase()
  if (!shouldRenderHtmlCardFence(normalizedLanguage, code) || !code) return null
  const parsed = parseHtmlCardFence(code)
  const cardId = parsed.cardId || `html-card-${keyHint}`

  if (options?.isStreaming) {
    return (
      <div
        key={cardId}
        className="text-muted-foreground not-prose my-4 rounded-2xl bg-muted/40 px-3 py-3 text-sm"
      >
        {parsed.fallback || 'Interactive card'}
        <span className="mt-1 block text-xs opacity-70">Rendering card…</span>
      </div>
    )
  }

  return (
    <HtmlCardHost
      key={cardId}
      cardId={cardId}
      html={parsed.html}
      fallback={parsed.fallback}
      onSubmit={onSubmit}
      initialState={options?.states?.[cardId]}
      onStateChange={(state) => options?.onStateChange?.(cardId, state)}
    />
  )
}

function GeneratedJSXPreview({
  bindings,
  isStreaming,
  jsx
}: {
  bindings: Record<string, unknown>
  isStreaming: boolean
  jsx: string
}) {
  return (
    <GeneratedPreviewActionsProvider>
      <JSXPreview
        bindings={bindings}
        className="not-prose my-3 text-foreground [color-scheme:light] dark:[color-scheme:dark]"
        components={JSX_PREVIEW_COMPONENTS}
        isStreaming={isStreaming}
        jsx={jsx}
      >
        <JSXPreviewContent className="min-w-0 [&_*]:max-w-full" />
        <JSXPreviewError className="mt-3" />
      </JSXPreview>
    </GeneratedPreviewActionsProvider>
  )
}

const savedHighlightSelector = 'mark[data-chat-highlight-id]'
const quoteHighlightSelector = 'mark[data-chat-quote-highlight="true"]'
const savedHighlightClassName =
  'box-decoration-clone rounded-[3px] bg-amber-200/80 px-0.5 text-inherit shadow-[0_0_0_1px_rgba(245,158,11,0.24),inset_0_-0.22em_0_rgba(245,158,11,0.35)] dark:bg-amber-400/25 dark:shadow-[0_0_0_1px_rgba(251,191,36,0.18),inset_0_-0.22em_0_rgba(251,191,36,0.22)]'
const quoteHighlightClassName = 'rounded bg-yellow-200 px-0.5 font-medium dark:bg-yellow-900/50'

// --------------

function HighlightQuotedText({
  containerRef,
  text
}: {
  containerRef: React.RefObject<HTMLDivElement | null>
  text: string
}) {
  React.useEffect(() => {
    const container = containerRef.current
    if (!container) return

    unwrapTextHighlightMarks(container, quoteHighlightSelector)

    if (text.trim()) {
      wrapTextHighlight(
        container,
        {
          text,
          startOffset: null,
          endOffset: null
        },
        {
          attributes: { 'data-chat-quote-highlight': 'true' },
          className: quoteHighlightClassName
        },
        { skipSelectors: [savedHighlightSelector, quoteHighlightSelector] }
      )
    }

    return () => {
      const currentContainer = containerRef.current
      if (currentContainer) {
        unwrapTextHighlightMarks(currentContainer, quoteHighlightSelector)
      }
    }
  }, [containerRef, text])

  return null
}

// --------------

function SavedMessageHighlights({
  containerRef,
  highlights,
  contentSignature
}: {
  containerRef: React.RefObject<HTMLDivElement | null>
  highlights: ChatHighlight[]
  contentSignature: string
}) {
  React.useEffect(() => {
    const container = containerRef.current
    if (!container) return

    unwrapTextHighlightMarks(container, savedHighlightSelector)

    highlights
      .filter((highlight) => highlight.text.trim().length > 0)
      .sort((a, b) => (b.startOffset ?? 0) - (a.startOffset ?? 0))
      .forEach((highlight) => {
        wrapTextHighlight(
          container,
          highlight,
          {
            attributes: { 'data-chat-highlight-id': highlight.id },
            className: savedHighlightClassName
          },
          { skipSelectors: [savedHighlightSelector] }
        )
      })
  }, [containerRef, contentSignature, highlights])

  return null
}

const MermaidControls = ({
  onMaximize,
  isDialogOpen,
  svg,
  onDownload
}: {
  onMaximize?: () => void
  isDialogOpen?: boolean
  svg?: string
  onDownload?: (format: 'svg' | 'png') => void
}) => {
  const { zoomIn, zoomOut, resetTransform } = useControls()
  const [showDownloadMenu, setShowDownloadMenu] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  const handleButtonClick = useCallback(
    (action: () => void) => (e: React.MouseEvent | React.TouchEvent) => {
      e.preventDefault()
      e.stopPropagation()
      action()
    },
    []
  )

  const handleDownloadClick = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setShowDownloadMenu((prev) => !prev)
  }, [])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowDownloadMenu(false)
      }
    }
    if (showDownloadMenu) {
      // Use setTimeout to avoid immediate closure
      setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside)
      }, 0)
      return () => {
        document.removeEventListener('mousedown', handleClickOutside)
      }
    }
  }, [showDownloadMenu])

  return (
    <div
      className={cn(
        'absolute left-2 top-2 z-[60] flex select-none gap-1',
        isDialogOpen && 'hidden'
      )}
    >
      <Button
        variant="outline"
        size="icon"
        className="size-8 cursor-pointer bg-background/50 backdrop-blur-sm"
        onClick={handleButtonClick(() => zoomIn(0.5))}
        onTouchEnd={handleButtonClick(() => zoomIn(0.5))}
      >
        <IconPlus size={16} />
      </Button>
      <Button
        variant="outline"
        size="icon"
        className="size-8 cursor-pointer bg-background/50 backdrop-blur-sm"
        onClick={handleButtonClick(() => zoomOut(0.5))}
        onTouchEnd={handleButtonClick(() => zoomOut(0.5))}
      >
        <IconMinus size={16} />
      </Button>
      <Button
        variant="outline"
        size="icon"
        className="size-8 cursor-pointer bg-background/50 backdrop-blur-sm"
        onClick={handleButtonClick(() => resetTransform())}
        onTouchEnd={handleButtonClick(() => resetTransform())}
      >
        <IconArrowsMaximize size={16} />
      </Button>
      {svg && onDownload && (
        <div className="relative" ref={menuRef}>
          <Button
            variant="outline"
            size="icon"
            className="size-8 cursor-pointer bg-background/50 backdrop-blur-sm"
            onClick={handleDownloadClick}
            onTouchEnd={handleDownloadClick}
          >
            <IconDownload size={16} />
          </Button>
          {showDownloadMenu && (
            <div className="absolute left-0 top-full z-[70] mt-1 flex flex-col gap-1 rounded-md border bg-background p-1 shadow-lg">
              <Button
                variant="ghost"
                size="sm"
                className="justify-start text-xs"
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  onDownload('svg')
                  setShowDownloadMenu(false)
                }}
              >
                Download SVG
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="justify-start text-xs"
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  onDownload('png')
                  setShowDownloadMenu(false)
                }}
              >
                Download PNG
              </Button>
            </div>
          )}
        </div>
      )}
      {onMaximize && (
        <Button
          variant="outline"
          size="icon"
          className="size-8 cursor-pointer bg-background/50 backdrop-blur-sm"
          onClick={handleButtonClick(onMaximize)}
          onTouchEnd={handleButtonClick(onMaximize)}
        >
          <IconMaximize size={16} />
        </Button>
      )}
    </div>
  )
}

const Controls = ({
  inDialog = false,
  svg,
  onMaximize,
  isDialogOpen,
  onDownload
}: {
  inDialog?: boolean
  svg?: string
  onMaximize?: () => void
  isDialogOpen?: boolean
  onDownload?: (format: 'svg' | 'png') => void
}) => {
  const { zoomIn, zoomOut, resetTransform } = useControls()

  const handleButtonClick = useCallback(
    (action: () => void) => (e: React.MouseEvent | React.TouchEvent) => {
      e.preventDefault()
      e.stopPropagation()
      action()
    },
    []
  )

  return (
    <>
      {!inDialog && (
        <MermaidControls
          onMaximize={onMaximize}
          isDialogOpen={isDialogOpen}
          svg={svg}
          onDownload={onDownload}
        />
      )}

      {inDialog && <MermaidControls svg={svg} onDownload={onDownload} />}
    </>
  )
}

const CodeBlockButton: FC<CodeBlock & { onClick: () => void; loading: boolean }> = ({
  language,
  filename,
  onClick,
  loading
}) => (
  <Button
    variant={'outline'}
    size={'lg'}
    className={
      'hover:opacity-1 flex h-auto max-w-[300px] items-center justify-start space-x-1 overflow-hidden rounded-lg p-3 text-left font-sans text-foreground hover:bg-transparent hover:shadow-md'
    }
    onClick={onClick}
  >
    <div className={cn('size-10')}>
      {loading ? <Loading /> : <FileIcon type={filename?.split('.')[1] || language} />}
    </div>
    <div className={'flex flex-col overflow-hidden'}>
      <div title={filename} className={'truncate'}>
        {filename}
      </div>
      <span className="line-clamp-1 text-ellipsis whitespace-pre-wrap text-xs font-normal text-foreground/60">
        Click to view file
      </span>
    </div>
  </Button>
)

const NoteButton: FC<{
  title: string
  preview: string
  onClick: () => void
  loading: boolean
}> = ({ title, preview, onClick, loading }) => (
  <Button
    variant={'outline'}
    size={'lg'}
    className={
      'hover:opacity-1 flex h-auto max-w-[300px] items-center justify-start space-x-1 overflow-hidden rounded-lg p-3 text-left font-sans text-foreground hover:bg-transparent hover:shadow-md'
    }
    onClick={onClick}
  >
    <div className={cn('flex size-10 items-center justify-center')}>
      {loading ? <Loading /> : <RiStickyNoteLine size={20} className="text-foreground" />}
    </div>
    <div className={'flex flex-col overflow-hidden'}>
      <div title={title} className={'max-w-[180px] truncate font-medium'}>
        {title}
      </div>
      <span className="line-clamp-2 text-ellipsis whitespace-pre-wrap text-xs font-normal text-foreground/60">
        {preview || 'Click to view note'}
      </span>
    </div>
  </Button>
)

const urlTransform: UrlTransform = (url, key, node) => {
  const safeUrl = getSafeMarkdownUrl(url)

  if (!safeUrl) {
    return ''
  }

  if (
    safeUrl.startsWith('space-item:') ||
    safeUrl.startsWith('connect:') ||
    safeUrl.startsWith('space-member:') ||
    safeUrl.startsWith('lead:') ||
    safeUrl.startsWith('contact:') ||
    safeUrl.startsWith('company:') ||
    // Chat mention schemes (@[Name](member:…), (assistant:…), …).
    // Without passthrough the href is blanked and Streamdown's
    // rehype-harden renders the mention as "Name [blocked]".
    safeUrl.startsWith('member:') ||
    safeUrl.startsWith('assistant:') ||
    safeUrl.startsWith('everyone:') ||
    safeUrl.startsWith('losi:') ||
    safeUrl.startsWith('staffing_agent:') ||
    safeUrl.startsWith('staffing-agent:') ||
    safeUrl.startsWith('super-agent:') ||
    safeUrl.startsWith('space_member:')
  ) {
    return safeUrl
  }

  if (safeUrl.startsWith('data:')) {
    return safeUrl
  }
  return defaultUrlTransform(safeUrl, key, node)
}

function getReactNodeText(node: React.ReactNode): string {
  if (typeof node === 'string' || typeof node === 'number') {
    return String(node)
  }

  if (Array.isArray(node)) {
    return node.map(getReactNodeText).join('')
  }

  if (React.isValidElement(node)) {
    const element = node as React.ReactElement<{ children?: React.ReactNode }>
    return getReactNodeText(element.props.children)
  }

  return ''
}

function escapeRegExp(value: string) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

const MARKDOWN_LINK_OR_IMAGE_REGEX = /(!?\[[^\]]*\]\([^)]*\))/g
const MARKDOWN_LINK_OR_IMAGE_SEGMENT_REGEX = /^!?\[[^\]]*\]\([^)]*\)$/

function getAnnotatedSpaceItemLinks(annotation: unknown): SidekickSpaceItemContext[] {
  if (!annotation || typeof annotation !== 'object' || Array.isArray(annotation)) {
    return []
  }

  const links = (annotation as Record<string, unknown>).space_item_links

  if (!Array.isArray(links)) {
    return []
  }

  return links.flatMap((item) => {
    if (!item || typeof item !== 'object' || Array.isArray(item)) {
      return []
    }

    const record = item as Record<string, unknown>
    const spaceId = typeof record.spaceId === 'string' ? record.spaceId : null
    const type = typeof record.type === 'string' ? record.type : null
    const id = typeof record.id === 'string' ? record.id : null
    const title = typeof record.title === 'string' ? record.title : null
    const summary = typeof record.summary === 'string' ? record.summary : null

    if (!spaceId || !type || !id || !title) {
      return []
    }

    return [
      {
        spaceId,
        type: type as SidekickSpaceItemContext['type'],
        id,
        title,
        summary
      }
    ]
  })
}

function linkRawSpaceItemId(content: string, item: SidekickSpaceItemContext) {
  const href = buildSidekickSpaceItemMarkdownHref(item)
  const linkedTitle = `[${item.title}](${href})`
  const escapedId = escapeRegExp(item.id)
  const rawIdRegex = new RegExp(`(^|[^\\w-])(\`?${escapedId}\`?)(?=$|[^\\w-])`, 'g')

  return content.replace(rawIdRegex, (_match, prefix: string) => {
    return `${prefix}${linkedTitle}`
  })
}

function linkAnnotatedSpaceItemIds(content: string, items: SidekickSpaceItemContext[]) {
  if (items.length === 0) {
    return content
  }

  return items.reduce((nextContent, item) => {
    return nextContent
      .split(MARKDOWN_LINK_OR_IMAGE_REGEX)
      .map((segment) => {
        if (MARKDOWN_LINK_OR_IMAGE_SEGMENT_REGEX.test(segment)) {
          return segment
        }

        return linkRawSpaceItemId(segment, item)
      })
      .join('')
  }, content)
}

// Native Presentation Slide Viewer Component
const PresentationSlideViewer: FC<{
  slides: Array<{
    viewerUrl?: string
    title?: string
    content?: string
    width?: number
    height?: number
    aspectRatio?: number
  }>
  title: string
  fileName: string
  onDownload: () => void
  onClose: () => void
}> = ({ slides, title, fileName, onDownload, onClose }) => {
  const [currentSlide, setCurrentSlide] = useState(0)

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % slides.length)
  }, [slides.length])

  const prevSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length)
  }, [slides.length])

  if (slides.length === 0) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-center">
          <IconPresentation className="mx-auto mb-4 size-12 text-gray-400" />
          <p className="text-gray-600">Loading slides...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="flex size-full flex-col bg-white">
      {/* Slide Display */}
      <div className="relative flex min-h-0 w-full flex-1 items-center justify-center bg-white">
        <iframe
          src={slides[currentSlide].viewerUrl}
          title={`Slide ${currentSlide + 1}`}
          className="size-full border-0 bg-white"
          allowFullScreen
          loading="lazy"
          style={{ minHeight: '100%' }}
        />

        {/* Navigation Arrows */}
        {slides.length > 1 && (
          <>
            <button
              onClick={prevSlide}
              className="absolute left-2 top-1/2 z-10 -translate-y-1/2 rounded-full bg-gray-800/80 p-2 text-white shadow-lg transition-colors hover:bg-gray-900/90 md:left-4 md:p-3"
              aria-label="Previous slide"
            >
              <IconChevronLeft className="size-4 md:size-6" />
            </button>
            <button
              onClick={nextSlide}
              className="absolute right-2 top-1/2 z-10 -translate-y-1/2 rounded-full bg-gray-800/80 p-2 text-white shadow-lg transition-colors hover:bg-gray-900/90 md:right-4 md:p-3"
              aria-label="Next slide"
            >
              <IconChevronRight className="size-4 md:size-6" />
            </button>
          </>
        )}

        {/* Slide Counter */}
        {slides.length > 1 && (
          <div className="absolute bottom-2 left-1/2 z-10 -translate-x-1/2 rounded-full bg-gray-800/80 px-2 py-1 text-xs text-white shadow-lg md:bottom-4 md:px-3 md:text-sm">
            {currentSlide + 1} / {slides.length}
          </div>
        )}
      </div>

      {/* Thumbnail Strip */}
      {slides.length > 1 && (
        <div className="flex h-16 items-center gap-2 overflow-x-auto border-t bg-gray-100 p-2 md:h-20">
          {slides.map((slide, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`h-10 w-12 shrink-0 overflow-hidden rounded border-2 transition-all md:h-12 md:w-16 ${
                index === currentSlide
                  ? 'scale-105 border-blue-500'
                  : 'border-gray-300 hover:border-gray-400'
              }`}
              aria-label={`Go to slide ${index + 1}`}
            >
              <div className="flex size-full items-center justify-center bg-gray-100 px-1 text-xs font-medium text-gray-600">
                {slide.title || `Slide ${index + 1}`}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

// Presentation Link Preview Component
const PresentationLinkPreview: FC<{
  chatId?: string
  url: string
  title: string
  fileName: string
  messageId?: string
  shouldTrackOutcome?: boolean
}> = ({ chatId, url, title, fileName, messageId, shouldTrackOutcome = false }) => {
  const [showPreview, setShowPreview] = useState(true)
  const [slides, setSlides] = useState<
    Array<{
      viewerUrl?: string
      title?: string
      content?: string
      width?: number
      height?: number
      aspectRatio?: number
    }>
  >([])
  const [loadingSlides, setLoadingSlides] = useState(true)

  const handleDownload = useCallback(() => {
    const link = document.createElement('a')
    link.href = url
    link.download = fileName
    link.target = '_blank'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    if (shouldTrackOutcome) {
      void trackMeaningfulOutcomeCompleted({
        surface: 'chat',
        outcome_type: 'saved_output',
        qualifying_action: 'exported',
        chat_id: chatId,
        message_id: messageId,
        source: 'presentation'
      })
    }
  }, [chatId, fileName, messageId, shouldTrackOutcome, url])

  // Auto-load slides on mount
  useEffect(() => {
    const loadSlides = async () => {
      if (slides.length === 0) {
        setLoadingSlides(true)
        try {
          // Call API to extract slides from the PPTX file
          const response = await fetch(
            `/api/presentation/extract-slides?url=${encodeURIComponent(url)}`
          )
          if (response.ok) {
            const data = await response.json()
            setSlides(data.slides || [])
          } else {
            const basicSlides = [
              {
                viewerUrl: `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}`,
                title: 'Presentation Slide',
                content:
                  'Slide content could not be extracted. Please try uploading as PDF for better results.'
              }
            ]
            setSlides(basicSlides)
          }
        } catch (error) {
          console.error('Failed to load slides:', error)
          // Fallback to Office viewer
          const fallbackSlides = [
            {
              viewerUrl: `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}`,
              title: 'Presentation',
              content: 'Loading presentation...'
            }
          ]
          setSlides(fallbackSlides)
        } finally {
          setLoadingSlides(false)
        }
      }
    }

    loadSlides()
  }, [url, slides.length])

  const togglePreview = useCallback(() => {
    setShowPreview(!showPreview)
  }, [showPreview])

  return (
    <div className="my-4 max-w-4xl">
      {/* Header with controls */}
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <IconPresentation className="size-5 text-blue-600" />
          <div>
            <div className="text-sm font-medium text-foreground">{title}</div>
            <div className="text-xs text-muted-foreground">{fileName}</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={togglePreview}
            className="text-xs text-muted-foreground transition-colors hover:text-foreground"
            title={showPreview ? 'Hide preview' : 'Show preview'}
          >
            {showPreview ? 'Hide' : 'Show'}
          </button>
          <button
            onClick={handleDownload}
            className="flex items-center gap-1 rounded bg-gray-500 px-3 py-1.5 text-xs text-white transition-colors hover:bg-gray-600"
            title="Download presentation"
          >
            <IconDownload className="size-3" />
            Download
          </button>
        </div>
      </div>

      {/* Auto-rendered Slide Preview */}
      {showPreview && (
        <div className="relative w-full overflow-hidden rounded-lg border border-border bg-white shadow-sm">
          <div
            className="relative max-h-[800px] min-h-[400px] w-full bg-white"
            style={
              slides.length > 0 && slides[0].aspectRatio
                ? { aspectRatio: `${slides[0].aspectRatio}` }
                : { aspectRatio: '16/9' }
            }
          >
            {loadingSlides ? (
              <div className="flex size-full items-center justify-center">
                <div className="text-center">
                  <div className="mx-auto mb-4 size-8 animate-spin rounded-full border-4 border-blue-500 border-t-transparent"></div>
                  <p className="text-sm text-gray-600">Loading presentation...</p>
                </div>
              </div>
            ) : slides.length > 0 ? (
              <PresentationSlideViewer
                slides={slides}
                title={title}
                fileName={fileName}
                onDownload={handleDownload}
                onClose={() => {}}
              />
            ) : (
              <div className="flex size-full items-center justify-center">
                <div className="text-center">
                  <IconPresentation className="mx-auto mb-4 size-12 text-gray-400" />
                  <p className="text-sm text-gray-600">Failed to load presentation</p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export const MermaidComponent = ({
  chatId,
  content,
  isStreaming = false,
  messageId,
  shouldTrackOutcome = false
}: {
  chatId?: string
  content: string
  isStreaming?: boolean
  messageId?: string
  shouldTrackOutcome?: boolean
}) => {
  // Deterministic element id derived from the diagram content (+messageId) so
  // Mermaid — which mutates the DOM directly via mermaid.run()/render() — always
  // targets the SAME node id across re-renders. A random id would change on any
  // re-render/remount and leave Mermaid manipulating stale nodes, which surfaced
  // as HierarchyRequestError / NotFoundError during the stream->final transition.
  const elementId = useMemo(() => {
    const seed = `${messageId ?? ''}:${content ?? ''}`
    let hash = 5381
    for (let i = 0; i < seed.length; i += 1) {
      hash = ((hash << 5) + hash + seed.charCodeAt(i)) | 0
    }
    return `mermaid-${(hash >>> 0).toString(36)}`
  }, [messageId, content])
  const [svg, setSvg] = useState<string>('')
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const renderAttempts = useRef(0)
  const maxRenderAttempts = 3

  const renderDiagram = useCallback(async () => {
    // If the content is still streaming, wait before attempting to render
    if (isStreaming) {
      // console.log('[MermaidComponent] Streaming in progress, waiting for complete diagram');
      setIsLoading(true)
      return
    }

    try {
      if (renderAttempts.current >= maxRenderAttempts) {
        throw new Error('Maximum render attempts reached')
      }

      setError(null)
      setIsLoading(true)

      // console.log('[MermaidComponent] Rendering diagram:', {
      //   contentPreview: content.substring(0, 100),
      //   elementId,
      //   attempt: renderAttempts.current + 1,
      //   mermaidInitialized
      // });

      if (!content || content.trim().length === 0) {
        throw new Error('Empty diagram content')
      }

      // Clean up the content by removing any Mermaid fence
      const cleanContent = content
        .replace(/^```mermaid\n?/, '')
        .replace(/```$/, '')
        .trim()

      if (typeof window === 'undefined') {
        // console.log('[MermaidComponent] Window is undefined, skipping render');
        return
      }

      // Lazy-load (and globally configure) mermaid on first diagram render
      const mermaid = await loadMermaid()

      const themeMode = getMessageMermaidThemeMode()

      // Initialize Mermaid with current theme settings
      if (!mermaidInitialized || mermaidThemeMode !== themeMode) {
        // console.log('[MermaidComponent] Attempting local initialization');
        await mermaid.initialize({
          startOnLoad: false,
          theme: themeMode === 'dark' ? 'dark' : 'default',
          securityLevel: 'loose',
          logLevel: process.env.NODE_ENV === 'development' ? 'debug' : 'error',
          fontFamily: 'inherit',
          themeVariables: getMessageMermaidThemeVariables(themeMode),
          flowchart: {
            // Prefer SVG text labels; foreignObject HTML labels are still allowed
            // through SafeSvgMarkup's tight HTML subset (mindmaps need them).
            htmlLabels: false,
            curve: 'basis',
            padding: 15,
            useMaxWidth: true
          },
          xyChart: {
            width: 900,
            height: 500,
            titleFontSize: 24,
            showTitle: true,
            titlePadding: 30,
            xAxis: {
              showLabel: true,
              labelFontSize: 16,
              labelPadding: 15,
              showTitle: true,
              titleFontSize: 18,
              titlePadding: 20,
              showTick: true,
              tickLength: 10,
              tickWidth: 2,
              showAxisLine: true,
              axisLineWidth: 2
            },
            yAxis: {
              showLabel: true,
              labelFontSize: 16,
              labelPadding: 15,
              showTitle: true,
              titleFontSize: 18,
              titlePadding: 20,
              showTick: true,
              tickLength: 10,
              tickWidth: 2,
              showAxisLine: true,
              axisLineWidth: 2
            }
          }
        })
        mermaidInitialized = true
        mermaidThemeMode = themeMode
        await mermaid.run({
          querySelector: `.mermaid-${elementId}`
        })
        // console.log('[MermaidComponent] Local initialization successful');
      }

      // Parse the diagram first to validate syntax
      try {
        // console.log('[MermaidComponent] Parsing diagram:', cleanContent);
        await mermaid.parse(cleanContent)
        // console.log('[MermaidComponent] Parse successful');
      } catch (parseError: any) {
        // console.error('[MermaidComponent] Parse error:', parseError);
        throw new Error(`Invalid diagram syntax: ${parseError?.message || 'Unknown error'}`)
      }

      // Render the diagram
      try {
        // console.log('[MermaidComponent] Rendering diagram with ID:', elementId);
        const { svg: renderedSvg } = await mermaid.render(elementId, cleanContent)
        // console.log('[MermaidComponent] Render successful, SVG length:', renderedSvg.length);
        setSvg(renderedSvg)
        renderAttempts.current = 0 // Reset attempts on success

        // Apply styles to the SVG after rendering
        requestAnimationFrame(() => {
          if (containerRef.current) {
            const svgElement = containerRef.current.querySelector('svg')
            if (svgElement) {
              svgElement.style.maxWidth = '100%'
              svgElement.style.height = 'auto'
              svgElement.style.minHeight = '100px'
              svgElement.style.margin = 'auto'
              svgElement.style.padding = '16px'
              svgElement.style.transition = 'all 0.3s ease'

              // Add subtle shadow in light mode
              if (!document.documentElement.classList.contains('dark')) {
                svgElement.style.filter = 'drop-shadow(0 4px 6px rgba(0, 0, 0, 0.1))'
              } else {
                // Subtle glow in dark mode
                svgElement.style.filter = 'drop-shadow(0 0 8px rgba(59, 130, 246, 0.2))'
              }

              // Add hover effects
              svgElement.addEventListener('mouseenter', () => {
                svgElement.style.transform = 'scale(1.01)'
                if (!document.documentElement.classList.contains('dark')) {
                  svgElement.style.filter = 'drop-shadow(0 8px 12px rgba(0, 0, 0, 0.15))'
                } else {
                  svgElement.style.filter = 'drop-shadow(0 0 12px rgba(59, 130, 246, 0.3))'
                }
              })

              svgElement.addEventListener('mouseleave', () => {
                svgElement.style.transform = 'scale(1)'
                if (!document.documentElement.classList.contains('dark')) {
                  svgElement.style.filter = 'drop-shadow(0 4px 6px rgba(0, 0, 0, 0.1))'
                } else {
                  svgElement.style.filter = 'drop-shadow(0 0 8px rgba(59, 130, 246, 0.2))'
                }
              })

              // Enhance specific elements
              const nodes = svgElement.querySelectorAll('.node')
              nodes.forEach((node: Element) => {
                if (node instanceof SVGElement) {
                  node.style.transition = 'all 0.3s ease'
                  node.addEventListener('mouseenter', () => {
                    node.style.transform = 'scale(1.05)'
                    node.style.filter = 'brightness(1.1)'
                  })
                  node.addEventListener('mouseleave', () => {
                    node.style.transform = 'scale(1)'
                    node.style.filter = 'none'
                  })
                }
              })

              // Add interactive edge hover effects
              const edgePaths = svgElement.querySelectorAll('.edgePath')
              edgePaths.forEach((edgePath: Element) => {
                if (edgePath instanceof SVGElement) {
                  // Extract node IDs from edge data attributes
                  const edgeData = edgePath.dataset
                  let sourceNodeId: string | null = null
                  let targetNodeId: string | null = null

                  // Try different Mermaid edge data patterns
                  if (edgeData.source) {
                    sourceNodeId = edgeData.source
                  } else if (edgeData.from) {
                    sourceNodeId = edgeData.from
                  }

                  if (edgeData.target) {
                    targetNodeId = edgeData.target
                  } else if (edgeData.to) {
                    targetNodeId = edgeData.to
                  }

                  // Fallback: parse from edge ID or class
                  if (!sourceNodeId || !targetNodeId) {
                    const edgeId = edgePath.id || edgePath.className.baseVal
                    const idMatch = edgeId.match(/edge_(\w+)_(\w+)/) || edgeId.match(/(\w+)-(\w+)/)
                    if (idMatch) {
                      sourceNodeId = idMatch[1]
                      targetNodeId = idMatch[2]
                    }
                  }

                  edgePath.addEventListener('mouseenter', () => {
                    // Highlight connected nodes
                    if (sourceNodeId) {
                      const sourceNode = svgElement.querySelector(
                        `.node[id*="${sourceNodeId}"], .node[id$="${sourceNodeId}"]`
                      )
                      if (sourceNode) {
                        sourceNode.classList.add('is-highlighted')
                      }
                    }
                    if (targetNodeId) {
                      const targetNode = svgElement.querySelector(
                        `.node[id*="${targetNodeId}"], .node[id$="${targetNodeId}"]`
                      )
                      if (targetNode) {
                        targetNode.classList.add('is-highlighted')
                      }
                    }
                  })

                  edgePath.addEventListener('mouseleave', () => {
                    // Remove highlighting from all nodes
                    const highlightedNodes = svgElement.querySelectorAll('.node.is-highlighted')
                    highlightedNodes.forEach((node) => node.classList.remove('is-highlighted'))
                  })
                }
              })

              // Add gradient backgrounds to clusters
              const clusters = svgElement.querySelectorAll('.cluster')
              clusters.forEach((cluster: Element, index: number) => {
                if (cluster instanceof SVGElement) {
                  const gradientId = `beautiful-gradient-${index}`
                  const gradient = document.createElementNS(
                    'http://www.w3.org/2000/svg',
                    'linearGradient'
                  )
                  gradient.setAttribute('id', gradientId)
                  gradient.append(
                    createSvgStop(
                      '0%',
                      document.documentElement.classList.contains('dark')
                        ? '#1e293b40'
                        : '#f8fafc40'
                    ),
                    createSvgStop(
                      '100%',
                      document.documentElement.classList.contains('dark')
                        ? '#0f172a40'
                        : '#f1f5f940'
                    )
                  )
                  svgElement.appendChild(gradient)
                  cluster.style.fill = `url(#${gradientId})`
                }
              })

              // Enhance edges with gradients
              const edges = svgElement.querySelectorAll('.edge')
              edges.forEach((edge: Element) => {
                if (edge instanceof SVGElement) {
                  edge.style.transition = 'all 0.3s ease'
                  const path = edge.querySelector('path')
                  if (path) {
                    path.style.strokeWidth = '2px'
                    path.style.strokeLinecap = 'round'
                    path.style.strokeLinejoin = 'round'
                  }
                }
              })

              // Enhance XY Chart elements with more sophisticated styling
              const chartPoints = svgElement.querySelectorAll('.point')
              chartPoints.forEach((point: Element) => {
                if (point instanceof SVGElement) {
                  point.style.transition = 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
                  point.style.filter = 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1))'
                  point.addEventListener('mouseenter', () => {
                    point.style.transform = 'scale(1.8)'
                    point.style.filter = 'drop-shadow(0 4px 8px rgba(0, 0, 0, 0.2)) brightness(1.3)'
                  })
                  point.addEventListener('mouseleave', () => {
                    point.style.transform = 'scale(1)'
                    point.style.filter = 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1))'
                  })
                }
              })

              // Enhanced gradient for chart areas
              const chartAreas = svgElement.querySelectorAll('.area')
              chartAreas.forEach((area: Element, index: number) => {
                if (area instanceof SVGElement) {
                  const gradientId = `chart-gradient-${index}`
                  const gradient = document.createElementNS(
                    'http://www.w3.org/2000/svg',
                    'linearGradient'
                  )
                  gradient.setAttribute('id', gradientId)
                  gradient.setAttribute('gradientTransform', 'rotate(90)')
                  gradient.append(
                    createSvgStop(
                      '0%',
                      document.documentElement.classList.contains('dark')
                        ? '#3b82f660'
                        : '#3b82f640'
                    ),
                    createSvgStop(
                      '50%',
                      document.documentElement.classList.contains('dark')
                        ? '#3b82f630'
                        : '#3b82f620'
                    ),
                    createSvgStop(
                      '100%',
                      document.documentElement.classList.contains('dark')
                        ? '#3b82f610'
                        : '#3b82f605'
                    )
                  )
                  svgElement.appendChild(gradient)
                  area.style.fill = `url(#${gradientId})`
                  area.style.filter = 'drop-shadow(0 4px 6px rgba(59, 130, 246, 0.1))'
                }
              })

              // Enhanced chart lines with sophisticated animation
              const chartLines = svgElement.querySelectorAll('.line')
              chartLines.forEach((line: Element) => {
                if (line instanceof SVGElement) {
                  line.style.transition = 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
                  line.style.strokeLinecap = 'round'
                  line.style.strokeLinejoin = 'round'
                  line.style.filter = 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1))'
                  line.addEventListener('mouseenter', () => {
                    line.style.strokeWidth = '5px'
                    line.style.filter = 'drop-shadow(0 4px 8px rgba(0, 0, 0, 0.2)) brightness(1.2)'
                  })
                  line.addEventListener('mouseleave', () => {
                    line.style.strokeWidth = '3px'
                    line.style.filter = 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1))'
                  })
                }
              })

              // Enhanced grid lines with more subtle animation
              const gridLines = svgElement.querySelectorAll('.grid-line')
              gridLines.forEach((gridLine: Element) => {
                if (gridLine instanceof SVGElement) {
                  gridLine.style.transition = 'all 0.3s ease'
                  gridLine.style.strokeDasharray = '6,6'
                  gridLine.style.strokeOpacity = '0.4'
                  gridLine.style.animation = 'gridDash 50s linear infinite'
                }
              })

              // Add enhanced grid animation
              if (!document.querySelector('#grid-animation')) {
                const style = document.createElement('style')
                style.id = 'grid-animation'
                style.textContent = `
                  @keyframes gridDash {
                    to {
                      stroke-dashoffset: -200;
                    }
                  }
                `
                document.head.appendChild(style)
              }

              // Add chart title enhancement
              const chartTitle = svgElement.querySelector('.chart-title')
              if (chartTitle instanceof SVGElement) {
                chartTitle.style.fontWeight = '600'
                chartTitle.style.letterSpacing = '0.5px'
                chartTitle.style.filter = 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1))'
              }

              // Add axis title enhancements
              const axisTitles = svgElement.querySelectorAll('.axis-title')
              axisTitles.forEach((title: Element) => {
                if (title instanceof SVGElement) {
                  title.style.fontWeight = '500'
                  title.style.letterSpacing = '0.3px'
                }
              })
            }
          }
        })
      } catch (renderError) {
        console.error('[MermaidComponent] Render error:', renderError)
        throw renderError
      }
    } catch (err) {
      // console.error('[MermaidComponent] Error:', err);
      setError(err instanceof Error ? err.message : 'Failed to render diagram')
      renderAttempts.current++

      if (renderAttempts.current < maxRenderAttempts) {
        // console.log(`[MermaidComponent] Retrying render (attempt ${renderAttempts.current + 1}/${maxRenderAttempts})`);
        setTimeout(() => renderDiagram(), 500)
      }
    } finally {
      setIsLoading(false)
    }
  }, [elementId, content, isStreaming])

  const handleDownload = useCallback(
    async (format: 'svg' | 'png') => {
      if (!svg) return

      try {
        if (format === 'svg') {
          // Download SVG directly
          const blob = new Blob([svg], { type: 'image/svg+xml' })
          const url = URL.createObjectURL(blob)
          const link = document.createElement('a')
          link.href = url
          link.download = `mermaid-diagram-${Date.now()}.svg`
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
          URL.revokeObjectURL(url)
          if (shouldTrackOutcome) {
            void trackMeaningfulOutcomeCompleted({
              surface: 'chat',
              outcome_type: 'saved_output',
              qualifying_action: 'exported',
              chat_id: chatId,
              message_id: messageId,
              source: 'mermaid_svg'
            })
          }
        } else if (format === 'png') {
          // Convert SVG to PNG
          const parser = new DOMParser()
          const svgDoc = parser.parseFromString(svg, 'image/svg+xml')
          const svgElement = svgDoc.querySelector('svg')
          if (!svgElement) {
            throw new Error('Could not parse SVG')
          }

          // Get SVG dimensions - try multiple methods
          let width = 800
          let height = 600

          // Try to get from viewBox
          const viewBox = svgElement.getAttribute('viewBox')
          if (viewBox) {
            const parts = viewBox.split(/\s+/)
            if (parts.length >= 4) {
              width = parseFloat(parts[2]) || width
              height = parseFloat(parts[3]) || height
            }
          }

          // Try to get from width/height attributes
          if (!viewBox || width === 800) {
            const widthAttr = svgElement.getAttribute('width')
            const heightAttr = svgElement.getAttribute('height')
            if (widthAttr) {
              width = parseFloat(widthAttr.replace(/[^\d.]/g, '')) || width
            }
            if (heightAttr) {
              height = parseFloat(heightAttr.replace(/[^\d.]/g, '')) || height
            }
          }

          // Try to get from DOM element if available
          const domSvgElement = containerRef.current?.querySelector('svg')
          if (domSvgElement) {
            try {
              const bbox = domSvgElement.getBBox()
              if (bbox.width > 0 && bbox.height > 0) {
                width = bbox.width
                height = bbox.height
              }
            } catch {
              // BBox might fail if SVG is not rendered yet
            }
          }

          // Ensure minimum dimensions
          if (width < 100) width = 800
          if (height < 100) height = 600

          // Create canvas with 2x resolution for better quality
          const scale = 2
          const canvas = document.createElement('canvas')
          canvas.width = width * scale
          canvas.height = height * scale
          const ctx = canvas.getContext('2d')
          if (!ctx) {
            throw new Error('Could not get canvas context')
          }

          // Set white background
          ctx.fillStyle = '#ffffff'
          ctx.fillRect(0, 0, canvas.width, canvas.height)

          // Create image from SVG
          const img = new Image()
          const svgBlob = new Blob([svg], { type: 'image/svg+xml' })
          const url = URL.createObjectURL(svgBlob)

          img.onload = () => {
            ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
            URL.revokeObjectURL(url)

            // Download PNG
            canvas.toBlob(
              (blob) => {
                if (!blob) {
                  throw new Error('Could not create PNG blob')
                }
                const pngUrl = URL.createObjectURL(blob)
                const link = document.createElement('a')
                link.href = pngUrl
                link.download = `mermaid-diagram-${Date.now()}.png`
                document.body.appendChild(link)
                link.click()
                document.body.removeChild(link)
                URL.revokeObjectURL(pngUrl)
                if (shouldTrackOutcome) {
                  void trackMeaningfulOutcomeCompleted({
                    surface: 'chat',
                    outcome_type: 'saved_output',
                    qualifying_action: 'exported',
                    chat_id: chatId,
                    message_id: messageId,
                    source: 'mermaid_png'
                  })
                }
              },
              'image/png',
              1.0
            )
          }

          img.onerror = () => {
            URL.revokeObjectURL(url)
            throw new Error('Failed to load SVG image')
          }

          img.src = url
        }
      } catch (error) {
        console.error('Download failed:', error)
        // You might want to show an error message to the user here
      }
    },
    [chatId, messageId, shouldTrackOutcome, svg]
  )

  useEffect(() => {
    // console.log('[MermaidComponent] Component mounted, attempting to render');
    renderDiagram()

    // Add theme change observer
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'class' && mutation.target instanceof HTMLElement) {
          // console.log('[MermaidComponent] Theme changed, re-rendering');
          renderDiagram()
        }
      })
    })

    if (typeof window !== 'undefined') {
      observer.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ['class']
      })
    }

    return () => observer.disconnect()
  }, [renderDiagram])

  if (error) {
    return (
      <div className="rounded-lg border border-red-300 bg-red-50 p-4 text-red-500 dark:bg-red-900/10">
        <p className="font-medium">Failed to render diagram:</p>
        <p className="mt-1 text-sm">{error}</p>
        <pre className="mt-2 overflow-auto rounded bg-gray-100 p-2 text-xs dark:bg-gray-800">
          <code>{content}</code>
        </pre>
      </div>
    )
  }

  return (
    <div className="my-4 overflow-hidden rounded-lg bg-transparent">
      <div ref={containerRef} className="mermaid-svg-container relative">
        {isLoading ? (
          <div className="flex animate-pulse space-x-4 p-4">
            <div className="flex-1 space-y-4 py-1">
              <div className="h-4 w-3/4 rounded bg-gray-200 dark:bg-gray-700"></div>
              <div className="space-y-2">
                <div className="h-4 rounded bg-gray-200 dark:bg-gray-700"></div>
                <div className="h-4 w-5/6 rounded bg-gray-200 dark:bg-gray-700"></div>
              </div>
            </div>
          </div>
        ) : svg ? (
          <>
            <TransformWrapper
              initialScale={1}
              minScale={0.1}
              maxScale={20}
              centerOnInit
              limitToBounds={false}
              wheel={{ disabled: true }}
              pinch={{ step: 5 }}
              doubleClick={{
                disabled: false,
                mode: 'zoomIn',
                step: 0.5,
                animationTime: 200
              }}
              alignmentAnimation={{ disabled: true }}
              velocityAnimation={{ disabled: true }}
            >
              {() => (
                <div className="relative w-full">
                  <Controls
                    svg={svg}
                    onMaximize={() => setIsDialogOpen(true)}
                    isDialogOpen={isDialogOpen}
                    onDownload={handleDownload}
                  />
                  <TransformComponent wrapperClass="!w-full" contentClass="!w-full">
                    <MermaidSvgHost className="flex w-full items-center justify-center" svg={svg} />
                  </TransformComponent>
                </div>
              )}
            </TransformWrapper>

            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen} modal={true}>
              <DialogContent
                className="flex size-full max-h-[95vh] max-w-[95vw] flex-col overflow-hidden p-0"
                onPointerDownOutside={(e) => e.preventDefault()}
                onInteractOutside={(e) => e.preventDefault()}
              >
                <div className="flex items-center justify-between border-b p-4">
                  <h2 className="text-lg font-semibold">Viewer</h2>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="size-8 cursor-pointer"
                    onClick={() => setIsDialogOpen(false)}
                    type="button"
                  >
                    <IconX size={16} />
                  </Button>
                </div>
                <div className="mermaid-svg-container relative flex-1 overflow-hidden bg-background">
                  <TransformWrapper
                    initialScale={1}
                    minScale={0.1}
                    maxScale={20}
                    centerOnInit
                    limitToBounds={false}
                    wheel={{ disabled: false, step: 0.1 }}
                    pinch={{ step: 5 }}
                    doubleClick={{
                      disabled: false,
                      mode: 'zoomIn',
                      step: 0.5,
                      animationTime: 200
                    }}
                    alignmentAnimation={{ disabled: true }}
                    velocityAnimation={{ disabled: true }}
                  >
                    {() => (
                      <div className="relative size-full select-none">
                        <Controls inDialog svg={svg} onDownload={handleDownload} />
                        <TransformComponent
                          wrapperClass="!w-full !h-full"
                          contentClass="!w-full !h-full"
                        >
                          <MermaidSvgHost
                            className="flex size-full items-center justify-center p-4"
                            svg={svg}
                          />
                        </TransformComponent>
                      </div>
                    )}
                  </TransformWrapper>
                </div>
              </DialogContent>
            </Dialog>
          </>
        ) : null}
      </div>
    </div>
  )
}

export default MermaidComponent

// Helper function to safely parse JSON
function parseSafely<T>(jsonString: string): T | null {
  try {
    return JSON.parse(jsonString) as T
  } catch {
    return null
  }
}

// --------------

function getMessageLinkRel(rel: unknown) {
  const tokens = new Set(typeof rel === 'string' ? rel.split(/\s+/).filter(Boolean) : [])
  tokens.add('noopener')
  tokens.add('noreferrer')

  return [...tokens].join(' ')
}

export const MessageMarkdown: FC<MessageMarkdownProps> = ({
  isGenerating = false,
  animateOnOpen = false,
  content,
  codeBlocks = [],
  onSelectCodeBlock,
  experimental_code_editor = false,
  messageId,
  onSelectMessage,
  message,
  highlights = [],
  onCreateHighlight,
  highlightQuoteText,
  onInteractiveSubmit
}) => {
  const contentWithoutSuggestions = stripAssistantResponseControlMarkers(content).trim()
  const shouldTrackChatOutputOutcome = message?.role === 'assistant'
  const outcomeChatId = message?.chat_id ?? undefined
  const outcomeMessageId = messageId
  const shouldAnimateMarkdown = isGenerating || animateOnOpen

  // Preprocess math delimiters
  const rawContent = contentWithoutSuggestions
    ?.replace(/\\\[([\s\S]*?)\\\]/g, (_, math) => `$$${math}$$`) // Convert display math
    ?.replace(/\\\(([\s\S]*?)\\\)/g, (_, math) => `$${math}$`) // Convert inline math

  const [selectedText, setSelectedText] = useState<string>('')
  const [selectionActionPosition, setSelectionActionPosition] =
    useState<SelectionActionPosition | null>(null)
  const [selectionCopied, setSelectionCopied] = useState(false)
  const messageSelectionRootRef = useRef<HTMLDivElement>(null)
  const proseContainerRef = useRef<HTMLDivElement>(null)
  const selectedRangeRef = useRef<Range | null>(null)
  const selectionUiVisibleRef = useRef(false)
  const selectionOwnerId = useId()
  const isCopyingSelectionRef = useRef(false)
  const selectionSyncFrameRef = useRef<number | null>(null)

  const router = useRouter()
  const pathname = usePathname()
  const { isSplitMode, navigateToHref } = useSplitPaneNavigation()
  const navigateToSupportedHref = useCallback(
    (href: string) => {
      if (isSplitMode) {
        navigateToHref(href)
        return
      }

      router.push(href)
    },
    [isSplitMode, navigateToHref, router]
  )
  // Markdown links to in-app routes navigate in place; only genuinely
  // external links keep the new-tab treatment in the renderers below.
  const getInAppLinkProps = useCallback(
    (hrefValue: string | undefined) => {
      const inAppHref = toInAppHref(hrefValue)

      if (!inAppHref) {
        return null
      }

      return {
        href: inAppHref,
        onClick: (event: React.MouseEvent<HTMLAnchorElement>) => {
          if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
            return
          }

          event.preventDefault()
          navigateToSupportedHref(inAppHref)
        }
      }
    },
    [navigateToSupportedHref]
  )

  const openSpaceItemFromMessage = useCallback(
    (item: SidekickSpaceItemContext) => {
      const wasHandled = dispatchSidekickOpenSpaceItem(item)

      if (wasHandled) {
        return
      }

      navigateToSupportedHref(getSidekickSpaceItemHref(item))
    },
    [navigateToSupportedHref]
  )

  // Plain links that point at a Space item (e.g. /spaces/:id?itemType=
  // task&itemId=… or /spaces/:id/designs/…) open the item modal in place,
  // like Space item cards do. If no modal handles it, openSpaceItemFromMessage
  // falls back to navigating to the item. The anchor keeps the canonical
  // item href so modifier-clicks / copy-link still work.
  const getSpaceItemLinkProps = useCallback(
    (hrefValue: string | undefined, title?: string | null) => {
      const inAppHref = toInAppHref(hrefValue)

      if (!inAppHref) {
        return null
      }

      const target = getSpaceItemFromHref(inAppHref)

      if (!target) {
        return null
      }

      const item: SidekickSpaceItemContext = {
        spaceId: target.spaceId,
        type: target.type,
        id: target.id,
        title: title?.trim() || 'Space item'
      }

      return {
        href: getSidekickSpaceItemHref(item),
        onClick: (event: React.MouseEvent<HTMLAnchorElement>) => {
          if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
            return
          }

          event.preventDefault()
          openSpaceItemFromMessage(item)
        }
      }
    },
    [openSpaceItemFromMessage]
  )
  const params = useParams()
  const routeChatId =
    typeof params?.chatid === 'string'
      ? params.chatid
      : Array.isArray(params?.chatid)
        ? params.chatid[0]
        : undefined
  const chatId = message?.chat_id ?? routeChatId
  const citationSources = useMemo(
    () => parseCitationSources(message?.citations),
    [message?.citations]
  )
  const annotatedSpaceItemLinks = useMemo(
    () => getAnnotatedSpaceItemLinks(message?.annotation),
    [message?.annotation]
  )

  // Track flashcard confidence states
  const [flashcardStates, setFlashcardStates] = useState<
    Record<string, 'correct' | 'incorrect' | null>
  >({})

  // HTML-card state survives iframe remounts (stream end, list
  // re-renders) instead of resetting the user's in-card progress, and
  // is persisted per message so a reload doesn't lose answers either.
  // (The card iframe itself never touches storage — host-side only.)
  const htmlCardStorageKey = messageId ? `losi-html-card-state:${messageId}` : null
  const [htmlCardStates, setHtmlCardStates] = useState<
    Record<string, HtmlCardState>
  >(() => {
    if (typeof window === 'undefined' || !htmlCardStorageKey) return {}
    try {
      const raw = window.localStorage.getItem(htmlCardStorageKey)
      const parsed = raw ? (JSON.parse(raw) as Record<string, HtmlCardState>) : {}
      return parsed && typeof parsed === 'object' ? parsed : {}
    } catch {
      return {}
    }
  })
  useEffect(() => {
    if (!htmlCardStorageKey) return
    try {
      if (Object.keys(htmlCardStates).length > 0) {
        window.localStorage.setItem(htmlCardStorageKey, JSON.stringify(htmlCardStates))
      } else {
        window.localStorage.removeItem(htmlCardStorageKey)
      }
    } catch {
      // storage full/blocked — in-memory state still works
    }
  }, [htmlCardStates, htmlCardStorageKey])
  const handleHtmlCardStateChange = useCallback(
    (cardId: string, state: HtmlCardState) => {
      setHtmlCardStates((prev) => ({ ...prev, [cardId]: state }))
    },
    []
  )

  // Handler for flashcard confidence changes
  const handleConfidenceChange = (id: string, confidence: ConfidenceLevel) => {
    setFlashcardStates((prev) => ({
      ...prev,
      [id]: confidence
    }))
  }

  const clearSelectionUi = useCallback((options?: { clearNativeSelection?: boolean }) => {
    if (selectionSyncFrameRef.current !== null) {
      window.cancelAnimationFrame(selectionSyncFrameRef.current)
      selectionSyncFrameRef.current = null
    }

    if (options?.clearNativeSelection) {
      window.getSelection()?.removeAllRanges()
    }

    if (!selectionUiVisibleRef.current && !options?.clearNativeSelection) return

    selectionUiVisibleRef.current = false
    selectedRangeRef.current = null
    setSelectedText('')
    setSelectionActionPosition(null)
    setSelectionCopied(false)
  }, [])

  const clearSelectedText = useCallback(() => {
    dispatchMessageTextSelectionClear()
    clearSelectionUi({ clearNativeSelection: true })
  }, [clearSelectionUi])

  const syncTextSelection = useCallback(
    (target?: EventTarget | Node | null) => {
      if (target && isMessageSelectionActionTarget(target)) {
        return
      }

      const selection = window.getSelection()
      const text = selection?.toString().trim()
      if (!selection || !text || selection.rangeCount === 0) {
        clearSelectionUi()
        return
      }

      const selectionRoot = getMessageTextSelectionElement(selection)
      if (!selectionRoot || selectionRoot !== messageSelectionRootRef.current) {
        clearSelectionUi()
        return
      }

      const range = selection.getRangeAt(0)
      const container = proseContainerRef.current
      if (
        !container ||
        !selection.anchorNode ||
        !selection.focusNode ||
        !container.contains(selection.anchorNode) ||
        !container.contains(selection.focusNode)
      ) {
        clearSelectionUi()
        return
      }

      const rect = getSelectionRect(range)
      dispatchMessageTextSelectionReplaced(selectionOwnerId)
      selectionUiVisibleRef.current = true
      selectedRangeRef.current = range.cloneRange()
      setSelectedText(text)
      setSelectionActionPosition(getSelectionActionPosition(rect))
      setSelectionCopied(false)
    },
    [clearSelectionUi, selectionOwnerId]
  )

  const queueTextSelectionSync = useCallback(
    (target?: EventTarget | Node | null) => {
      if (selectionSyncFrameRef.current !== null) {
        window.cancelAnimationFrame(selectionSyncFrameRef.current)
      }

      selectionSyncFrameRef.current = window.requestAnimationFrame(() => {
        selectionSyncFrameRef.current = null
        syncTextSelection(target)
      })
    },
    [syncTextSelection]
  )

  const handleTextSelection = (
    event: React.TouchEvent<HTMLDivElement> | React.MouseEvent<HTMLDivElement>
  ) => {
    queueTextSelectionSync(event.target)
  }

  const handleCopySelection = useCallback(async () => {
    const text = selectedText.trim()
    if (!text) return

    isCopyingSelectionRef.current = true
    let copied = false
    try {
      copied = await copyPlainText(text)
    } finally {
      isCopyingSelectionRef.current = false
    }
    if (!copied) {
      return
    }

    setSelectionCopied(true)
    if (message?.role === 'assistant' && messageId) {
      void trackMeaningfulOutcomeCompleted({
        surface: 'chat',
        outcome_type: 'saved_output',
        qualifying_action: 'copied',
        message_id: messageId,
        source: 'assistant_selection'
      })
    }
  }, [message?.role, messageId, selectedText])

  const handleReplyToSelection = useCallback(() => {
    const text = selectedText.trim()
    if (!text) return

    window.dispatchEvent(
      new CustomEvent('quoteText', {
        detail: { text, messageId, chatId }
      })
    )
    clearSelectedText()
  }, [chatId, clearSelectedText, messageId, selectedText])

  const handleHighlightSelection = useCallback(() => {
    const text = selectedText.trim()
    const container = proseContainerRef.current
    const range = selectedRangeRef.current
    if (!text || !messageId || !onCreateHighlight) return

    const offsets =
      container && range
        ? getSelectionOffsets(container, range, text)
        : { startOffset: null, endOffset: null, prefix: '', suffix: '' }

    onCreateHighlight({
      messageId,
      text,
      startOffset: offsets.startOffset,
      endOffset: offsets.endOffset,
      prefix: offsets.prefix,
      suffix: offsets.suffix
    })
    clearSelectedText()
  }, [clearSelectedText, messageId, onCreateHighlight, selectedText])

  const hasActiveSelectionInMessage = useCallback(() => {
    const selection = window.getSelection()
    const selectionRoot = selection ? getMessageTextSelectionElement(selection) : null

    return Boolean(
      selection &&
        selection.rangeCount > 0 &&
        selectionRoot &&
        selectionRoot === messageSelectionRootRef.current
    )
  }, [])

  useEffect(() => {
    const handleSelectionReplaced = (event: Event) => {
      const detail = (event as CustomEvent<MessageTextSelectionReplacedDetail>).detail
      if (detail?.ownerId !== selectionOwnerId) {
        clearSelectionUi()
      }
    }

    const handleSelectionClear = () => clearSelectionUi()

    window.addEventListener(MESSAGE_TEXT_SELECTION_REPLACED_EVENT, handleSelectionReplaced)
    window.addEventListener(MESSAGE_TEXT_SELECTION_CLEAR_EVENT, handleSelectionClear)

    return () => {
      window.removeEventListener(MESSAGE_TEXT_SELECTION_REPLACED_EVENT, handleSelectionReplaced)
      window.removeEventListener(MESSAGE_TEXT_SELECTION_CLEAR_EVENT, handleSelectionClear)
    }
  }, [clearSelectionUi, selectionOwnerId])

  useEffect(() => {
    const handleSelectionChange = () => {
      if (isCopyingSelectionRef.current) return
      if (!hasActiveSelectionInMessage()) {
        if (selectionUiVisibleRef.current) {
          clearSelectionUi()
        }
        return
      }

      queueTextSelectionSync()
    }

    document.addEventListener('selectionchange', handleSelectionChange)

    return () => {
      document.removeEventListener('selectionchange', handleSelectionChange)
    }
  }, [clearSelectionUi, hasActiveSelectionInMessage, queueTextSelectionSync])

  useEffect(() => {
    const handleSelectionCommit = (event: MouseEvent | KeyboardEvent | TouchEvent) => {
      if (isMessageSelectionActionTarget(event.target)) return

      queueTextSelectionSync(event.target)
    }

    document.addEventListener('mouseup', handleSelectionCommit)
    document.addEventListener('keyup', handleSelectionCommit)
    document.addEventListener('touchend', handleSelectionCommit)

    return () => {
      document.removeEventListener('mouseup', handleSelectionCommit)
      document.removeEventListener('keyup', handleSelectionCommit)
      document.removeEventListener('touchend', handleSelectionCommit)
    }
  }, [queueTextSelectionSync])

  useEffect(() => {
    return () => {
      if (selectionSyncFrameRef.current !== null) {
        window.cancelAnimationFrame(selectionSyncFrameRef.current)
      }
    }
  }, [])

  useEffect(() => {
    if (!selectedText) return

    const handleSelectionScroll = () => {
      clearSelectionUi()
    }

    document.addEventListener('scroll', handleSelectionScroll, true)

    return () => {
      document.removeEventListener('scroll', handleSelectionScroll, true)
    }
  }, [clearSelectionUi, selectedText])

  const handleCodeBlockClick = useCallback(
    (codeBlock: CodeBlock) => {
      onSelectCodeBlock?.(codeBlock)
    },
    [onSelectCodeBlock]
  )

  const handleNoteClick = useCallback(() => {
    if (message) {
      onSelectMessage?.(message)
    }
  }, [onSelectMessage, message])
  const messageMarkdownCustomComponents = useMemo(
    () => ({
      'space-item-card': (props: MessageMarkdownCustomTagProps) => {
        const payloadText = readMessageMarkdownPayload(props)
        const isLoading =
          props['data-loading'] === '1' ||
          props['data-loading'] === true ||
          props['data-loading'] === ''

        if (isLoading || !payloadText.trim()) {
          return (
            <div
              aria-hidden
              className="not-prose my-1 h-10 w-full max-w-full animate-pulse rounded-md bg-muted/40"
            />
          )
        }

        const item = parseSidekickSpaceItemCardPayload(payloadText)
        if (!item) return null

        return (
          <SpaceItemMessageCard
            className="not-prose my-1 max-w-full"
            href={getSidekickSpaceItemHref(item)}
            item={item}
            onOpen={openSpaceItemFromMessage}
          />
        )
      },
      'get-premium-button': (props: MessageMarkdownCustomTagProps) => (
        <GetPremiumMarkdownButton
          className="not-prose my-2"
          payloadText={readMessageMarkdownPayload(props)}
        />
      ),
      'losi-app-preview': (props: MessageMarkdownCustomTagProps) => {
        const payload = parseLosiAppPreviewMarkerPayload(readMessageMarkdownPayload(props))
        if (!payload) return null

        return <ChatAppPreviewCard payload={payload} />
      },
      'losi-planner-day': (props: MessageMarkdownCustomTagProps) => {
        const payload = parseLosiPlannerDayMarkerPayload(readMessageMarkdownPayload(props))
        if (!payload) return null

        return <LosiPlannerDayCard payload={payload} />
      }
    }),
    [openSpaceItemFromMessage]
  )

  // Extract quote data from content if it exists
  // Use contentWithoutSuggestions so suggestion markers never leak into messageContent
  const { items: spaceItemContexts, contentWithoutTags: contentWithoutSpaceItemTags } =
    parseSidekickSpaceItemTags(contentWithoutSuggestions)
  const quoteMatch = contentWithoutSpaceItemTags.match(/\[QUOTE:(.*?)\](.*?)\[\/QUOTE\]/s)
  const rawMessageContent = quoteMatch
    ? contentWithoutSpaceItemTags.replace(/\[QUOTE:(.*?)\](.*?)\[\/QUOTE\]/s, '')
    : contentWithoutSpaceItemTags
  // While a message streams in, a [TREND] block arrives before its closing
  // [/TREND] tag. If that partial raw JSON is rendered as markdown it lands
  // inside a <p>, and when the completed block later swaps to the block-level
  // TrendMessageCard <div> at the same position React's DOM commit fails with
  // HierarchyRequestError / NotFoundError. Strip any *unterminated* trend block
  // so only complete blocks ever reach the component-marker path below. The
  // model still receives the full payload; this only affects the rendered text.
  const contentWithoutPartialTrend = rawMessageContent.replace(
    /\[TREND\](?:(?!\[\/TREND\])[\s\S])*$/,
    ''
  )
  const spaceItemCardTags = spaceItemContexts
    .map((item) => serializeSidekickSpaceItemCardTag(item))
    .join('\n\n')
  const linkedMessageContent = linkAnnotatedSpaceItemIds(
    contentWithoutPartialTrend,
    annotatedSpaceItemLinks
  )
  const messageContent = repairBrokenMarkdownTables(
    replaceSpaceItemCardMarkers(
      replacePremiumCtaMarkers(
        spaceItemCardTags ? `${spaceItemCardTags}\n\n${linkedMessageContent}` : linkedMessageContent
      )
    )
  )

  // Check if this message is a note
  const isNote = REGEX_NOTE.test(messageContent)

  // If it's a note, render as NoteButton instead of full content
  if (isNote && experimental_code_editor) {
    const lines = messageContent.split(/\r?\n/)
    const header = lines[0] || ''
    const titleMatch = header.match(REGEX_NOTE)
    const title = titleMatch?.[1]?.trim() || 'Note'
    const body = lines.slice(1).join('\n').trim()
    const preview = body.length > 100 ? body.substring(0, 100) + '...' : body

    return (
      <div className="my-2">
        <NoteButton
          title={title}
          preview={preview}
          onClick={handleNoteClick}
          loading={!!isGenerating}
        />
      </div>
    )
  }

  // Process the content to handle special components and maintain their positions
  const processContent = (
    content: string
  ): {
    processedContent: string
    componentMarkers: { marker: string; component: React.ReactNode; originalText: string }[]
  } => {
    // Create a replacement map to track where each component should be inserted
    const componentMarkers: {
      marker: string
      component: React.ReactNode
      originalText: string
    }[] = []

    let processedContent = content
    let index = 0

    // Process each type of interactive component, preserving their positions
    const premiumCtaMatches = findPremiumCtaMarkers(content)
    premiumCtaMatches.forEach((premiumCta) => {
      const marker = `__COMPONENT_MARKER_${index}__`
      const component = (
        <GetPremiumMarkdownButton
          className="not-prose my-2"
          key={`get-premium-button-${index}`}
          payloadText={premiumCta.payloadText}
        />
      )
      componentMarkers.push({ marker, component, originalText: premiumCta.rawText })
      index++
    })

    // Detect "My location is: lat, lng" and render as map card
    const locationMatches = Array.from(
      content.matchAll(/My location is:\s*([-\d.]+),\s*([-\d.]+)/g)
    )
    locationMatches.forEach((match) => {
      const lat = parseFloat(match[1])
      const lng = parseFloat(match[2])
      if (isNaN(lat) || isNaN(lng)) return

      const marker = `__COMPONENT_MARKER_${index}__`
      const component = (
        <LocationSharedCard
          key={`location-shared-${index}`}
          latitude={lat}
          longitude={lng}
        />
      )
      componentMarkers.push({ marker, component, originalText: match[0] })
      index++
    })

    // Space item cards:
    // - Inside table rows: already converted to <space-item-card> (keeps GFM intact)
    // - Standalone [SPACE_ITEM_CARD]: extract to COMPONENT_MARKER (proven render path)
    const SPACE_ITEM_CARD_BRACKET_REGEX =
      /\\?\[SPACE_ITEM_CARD\\?\]([\s\S]*?)\\?\[\/SPACE_ITEM_CARD\\?\]/g
    const spaceItemCardMatches = Array.from(
      content.matchAll(SPACE_ITEM_CARD_BRACKET_REGEX)
    )

    spaceItemCardMatches.forEach((match) => {
      const item = parseSidekickSpaceItemCardPayloadLenient(match[1] || '')
      if (!item) return

      const marker = `__COMPONENT_MARKER_${index}__`
      const component = (
        <SpaceItemMessageCard
          className="not-prose my-2 max-w-full"
          href={getSidekickSpaceItemHref(item)}
          item={item}
          key={`space-item-card-${index}`}
          onOpen={openSpaceItemFromMessage}
        />
      )
      componentMarkers.push({ marker, component, originalText: match[0] })
      index++
    })

    // Also lift standalone <space-item-card> custom elements (not in a table
    // row) into COMPONENT_MARKER so they always render as React cards.
    const SPACE_ITEM_CARD_ELEMENT_REGEX =
      /<space-item-card\b([^>]*)><\/space-item-card>/gi
    const spaceItemCardElementMatches = Array.from(
      content.matchAll(SPACE_ITEM_CARD_ELEMENT_REGEX)
    )

    spaceItemCardElementMatches.forEach((match) => {
      const matchStart = match.index ?? -1
      if (matchStart < 0) return

      const lineStart = content.lastIndexOf('\n', matchStart - 1) + 1
      const nextBreak = content.indexOf('\n', matchStart)
      const lineEnd = nextBreak === -1 ? content.length : nextBreak
      const line = content.slice(lineStart, lineEnd)
      const localStart = matchStart - lineStart
      const before = line.slice(0, localStart)
      const after = line.slice(localStart + match[0].length)
      if (before.includes('|') || after.includes('|')) {
        // Leave in-table custom elements for Streamdown cell rendering.
        return
      }

      const attrs = match[1] || ''
      const payloadMatch = attrs.match(
        /\bdata-payload\s*=\s*(?:"([^"]*)"|'([^']*)')/i
      )
      const rawPayload = payloadMatch?.[1] || payloadMatch?.[2] || ''
      let payloadText = rawPayload
      try {
        payloadText = decodeURIComponent(rawPayload)
      } catch {
        payloadText = rawPayload
      }

      if (!payloadText.trim() || /\bdata-loading\b/i.test(attrs)) {
        return
      }

      const item = parseSidekickSpaceItemCardPayload(payloadText)
      if (!item) return

      const marker = `__COMPONENT_MARKER_${index}__`
      const component = (
        <SpaceItemMessageCard
          className="not-prose my-2 max-w-full"
          href={getSidekickSpaceItemHref(item)}
          item={item}
          key={`space-item-card-el-${index}`}
          onOpen={openSpaceItemFromMessage}
        />
      )
      componentMarkers.push({ marker, component, originalText: match[0] })
      index++
    })

    // Trend cards ([TREND]{…}[/TREND]) — rich context sent from the marketing
    // trending handoff (mirrors the mobile app's TrendComponent). Parse the
    // JSON payload and render a trend card in place of the raw block.
    const trendMatches = Array.from(content.matchAll(/\[TREND\]([\s\S]*?)\[\/TREND\]/g))

    trendMatches.forEach((match) => {
      const marker = `__COMPONENT_MARKER_${index}__`
      let payload: {
        query?: string
        region?: string
        timeframe?: string
        source?: string
        picture?: string
        pictureSource?: string
        traffic?: number
        newsItems?: {
          title: string
          url?: string
          snippet?: string
          picture?: string
          source?: string
        }[]
        description?: string
      } | null = null
      try {
        payload = JSON.parse(match[1] || '')
      } catch {
        payload = null
      }
      // Skip rendering when the payload is unusable, but still consume the raw
      // block so the JSON never renders. Use a real (unique) marker mapped to an
      // empty fragment and keep `index` monotonic — pushing without incrementing
      // caused a later component to reuse this marker token, which made React
      // commit a tree that didn't match the DOM (HierarchyRequestError).
      if (!payload?.query) {
        componentMarkers.push({
          marker,
          component: <React.Fragment key={`trend-skip-${index}`} />,
          originalText: match[0]
        })
        index++
        return
      }

      const component = (
        <TrendMessageCard key={`trend-card-${index}`} data={{ ...payload, query: payload.query }} />
      )
      componentMarkers.push({ marker, component, originalText: match[0] })
      index++
    })

    const losiAppPreviewMatches = Array.from(content.matchAll(LOSI_APP_PREVIEW_MARKER_REGEX))

    losiAppPreviewMatches.forEach((match) => {
      const payload = parseLosiAppPreviewMarkerPayload(match[1] || '')
      if (!payload) return

      const marker = `__COMPONENT_MARKER_${index}__`
      const component = <ChatAppPreviewCard key={`losi-app-preview-${index}`} payload={payload} />
      componentMarkers.push({ marker, component, originalText: match[0] })
      index++
    })

    const losiPlannerDayMatches = Array.from(content.matchAll(LOSI_PLANNER_DAY_MARKER_REGEX))

    losiPlannerDayMatches.forEach((match) => {
      const payload = parseLosiPlannerDayMarkerPayload(match[1] || '')
      if (!payload) return

      const marker = `__COMPONENT_MARKER_${index}__`
      const component = <LosiPlannerDayCard key={`losi-planner-day-${index}`} payload={payload} />
      componentMarkers.push({ marker, component, originalText: match[0] })
      index++
    })

    const youtubeVideoListMatches = Array.from(content.matchAll(YOUTUBE_VIDEO_LIST_MARKER_REGEX))

    youtubeVideoListMatches.forEach((match) => {
      const list = parseYoutubeVideoListMarkerPayload(match[1] || '')
      if (!list) return

      const marker = `__COMPONENT_MARKER_${index}__`
      const component = (
        <YouTubeVisualizer
          key={`youtube-video-list-${index}`}
          title={list.title}
          videos={list.videos}
        />
      )
      componentMarkers.push({ marker, component, originalText: match[0] })
      index++
    })

    const youtubeVideoMatches = Array.from(content.matchAll(YOUTUBE_VIDEO_MARKER_REGEX))

    youtubeVideoMatches.forEach((match) => {
      const video = parseYoutubeVideoMarkerPayload(match[1] || '')
      if (!video) return

      const marker = `__COMPONENT_MARKER_${index}__`
      const component = <YouTubeVisualizer key={`youtube-video-${index}`} videos={[video]} />
      componentMarkers.push({ marker, component, originalText: match[0] })
      index++
    })

    const visualArtifactMatches = Array.from(content.matchAll(VISUAL_ARTIFACT_MARKER_REGEX))

    visualArtifactMatches.forEach((match) => {
      const marker = `__COMPONENT_MARKER_${index}__`
      const payload = match[1] || ''
      const artifact = parseVisualArtifactMarkerPayload(payload)
      const component = artifact ? (
        <VisualArtifactRenderer
          artifact={artifact}
          className="my-4"
          key={`visual-artifact-${index}`}
        />
      ) : (
        <InvalidVisualArtifactFallback
          key={`visual-artifact-invalid-${index}`}
          payload={payload}
        />
      )
      componentMarkers.push({ marker, component, originalText: match[0] })
      index++
    })

    // Flashcards
    const flashcardMatches = Array.from(
      content.matchAll(/\[FLASHCARD\](.*?)\|(.*?)\[\/FLASHCARD\]/gs)
    )
    const flashcardItems: FlashcardItem[] = []

    flashcardMatches.forEach((match, idx) => {
      const marker = `__COMPONENT_MARKER_${index}__`
      const question = match[1]?.trim() || ''
      const answer = match[2]?.trim() || ''
      const flashcardId = `flashcard-${messageId || 'unknown'}-${idx}`

      // Add item to the flashcard collection
      flashcardItems.push({ question, answer })

      const component = (
        <Flashcard
          key={`flashcard-${index}`}
          id={flashcardId}
          question={question}
          answer={answer}
          initialConfidence={flashcardStates[flashcardId] || null}
          onConfidenceChange={(confidence) => handleConfidenceChange(flashcardId, confidence)}
        />
      )
      componentMarkers.push({ marker, component, originalText: match[0] })
      index++
    })

    // Add focus mode button if there are multiple flashcards
    if (flashcardItems.length > 1) {
      const marker = `__COMPONENT_MARKER_${index}__`
      const component = (
        <div key={`flashcard-focus-button-${index}`} className="flex justify-end">
          <FlashcardFocusButton flashcards={flashcardItems} />
        </div>
      )
      componentMarkers.push({ marker, component, originalText: '' })
      index++
    }

    // Todo lists (expandable to modal). Match ALL blocks — `.match()` only
    // kept the first, so a second `[TODO]…[/TODO]` (often with `[x]` completed
    // prefixes) was left as raw tags in the message.
    const todoListMatches = Array.from(content.matchAll(/\[TODO\]([\s\S]*?)\[\/TODO\]/gi))
    todoListMatches.forEach((match) => {
      const todoItems = match[1]
        .split('|')
        .map((item) => item.trim())
        .filter((item) => item.length > 0)
      if (todoItems.length === 0) return

      const marker = `__COMPONENT_MARKER_${index}__`
      const component = (
        <ExpandableInteractiveWrapper key={`todo-${index}`} title="Todo List">
          <TodoList items={todoItems} />
        </ExpandableInteractiveWrapper>
      )
      componentMarkers.push({ marker, component, originalText: match[0] })
      index++
    })

    // Quiz questions
    const quizMatches = Array.from(content.matchAll(/\[QUIZ\](.*?)\[\/QUIZ\]/gs))
    const quizItems: QuestionItem[] = []

    quizMatches.forEach((match) => {
      // Normalize the quiz content by removing extra characters and fixing common formatting issues
      let quizContent = match[0]
        .replace(/\[QUIZ\]|\[\/QUIZ\]/g, '')
        // Fix common formatting errors
        .replace(/\|\[UNDERSTAND\]/g, '[UNDERSTAND]') // Remove extra pipe before [UNDERSTAND]
        .replace(/\[\/CATEGORY\]/g, '[CATEGORY]') // Fix incorrect closing tag
        .replace(/\|(?=\[)/g, '') // Remove any pipe before a bracket

      // Split into parts - more robust approach
      const parts = quizContent.split('|')
      const question = parts[0].trim()

      // Determine where the answer and explanation are
      let answerIndex, explanationIndex
      const correctAnswerPattern = /^[A-D]$/

      // Find the answer (assuming it's a single letter A-D)
      for (let i = 1; i < parts.length; i++) {
        if (correctAnswerPattern.test(parts[i].trim())) {
          answerIndex = i
          explanationIndex = i + 1
          break
        }
      }

      // If we couldn't find the answer in the normal way, assume it's the second-to-last part
      if (!answerIndex) {
        answerIndex = parts.length - 2
        explanationIndex = parts.length - 1
      }

      const correctAnswer = parts[answerIndex]?.trim() || ''
      // Extract the explanation and split into parts
      let explanationText = ''
      if (explanationIndex !== undefined && explanationIndex < parts.length) {
        explanationText = parts[explanationIndex]
      }

      // Parse out the different parts of the explanation
      const explanationParts = explanationText.split(
        /\[(?:UNDERSTAND|CATEGORY|DIFFICULTY|FEEDBACK)\]/
      )
      const explanation = explanationParts[0].trim()

      // Extract the remaining special sections using regex
      let conceptExplanationText = ''
      let categoryTag = undefined
      let difficultyLevel = undefined
      let feedback = undefined

      // Extract concept explanation (the part after [UNDERSTAND])
      const understandMatch = explanationText.match(/\[UNDERSTAND\](.*?)(?:\[|$)/s)
      if (understandMatch) {
        conceptExplanationText = understandMatch[1].trim()
      }

      // Extract category
      const categoryMatch = explanationText.match(/\[CATEGORY\](.*?)(?:\[|$)/s)
      if (categoryMatch) {
        categoryTag = categoryMatch[1].trim()
      }

      // Extract difficulty
      const difficultyMatch = explanationText.match(/\[DIFFICULTY\](.*?)(?:\[|$)/s)
      if (difficultyMatch) {
        difficultyLevel = difficultyMatch[1].trim().toLowerCase()
        if (!['easy', 'medium', 'hard'].includes(difficultyLevel)) {
          difficultyLevel = 'medium'
        }
      }

      // Extract feedback
      const feedbackMatch = explanationText.match(/\[FEEDBACK\](.*?)(?:\[|$)/s)
      if (feedbackMatch) {
        feedback = feedbackMatch[1].trim()
      }

      // Get the options (everything between question and answer)
      const options = parts.slice(1, answerIndex).map((opt) => opt.trim())

      // Add item to the quiz collection
      quizItems.push({
        question,
        options,
        correctAnswer,
        explanation,
        conceptExplanation: conceptExplanationText || undefined,
        categoryTag,
        difficultyLevel: difficultyLevel as 'easy' | 'medium' | 'hard' | undefined
      })

      // Create the component as before
      const marker = `__COMPONENT_MARKER_${index}__`
      const component = (
        <QuizQuestion
          key={`quiz-${index}`}
          question={question}
          options={options}
          correctAnswer={correctAnswer}
          explanation={explanation}
          conceptExplanation={conceptExplanationText || undefined}
          categoryTag={categoryTag}
          difficultyLevel={difficultyLevel as 'easy' | 'medium' | 'hard' | undefined}
          feedback={feedback}
          onScoreUpdate={(result) => safeConsole.log('Quiz result:', result)}
        />
      )
      componentMarkers.push({ marker, component, originalText: match[0] })
      index++
    })

    // Add focus mode button if there are multiple quiz questions
    if (quizItems.length > 1) {
      const marker = `__COMPONENT_MARKER_${index}__`
      const component = (
        <div key={`quiz-focus-button-${index}`} className="flex justify-end">
          <QuestionFocusButton questions={quizItems} />
        </div>
      )
      componentMarkers.push({ marker, component, originalText: '' })
      index++
    }

    // Timeline
    const timelineMatches = Array.from(content.matchAll(/\[TIMELINE\](.*?)\[\/TIMELINE\]/gs))
    timelineMatches.forEach((match) => {
      try {
        const data = JSON.parse(match[1])
        const marker = `__COMPONENT_MARKER_${index}__`
        const component = (
          <Timeline key={`timeline-${index}`} events={data.events} className={data.className} />
        )
        componentMarkers.push({ marker, component, originalText: match[0] })
        index++
      } catch (e) {
        safeConsole.error('Failed to parse timeline data:', e)
      }
    })

    // Video storyboard
    const storyboardMatches = Array.from(content.matchAll(/\[STORYBOARD\](.*?)\[\/STORYBOARD\]/gs))
    storyboardMatches.forEach((match) => {
      try {
        const data = JSON.parse(match[1]) as unknown
        const marker = `__COMPONENT_MARKER_${index}__`
        const component = <VideoStoryboard key={`storyboard-${index}`} data={data} />
        componentMarkers.push({ marker, component, originalText: match[0] })
        index++
      } catch (e) {
        safeConsole.error('Failed to parse storyboard data:', e)
        const marker = `__COMPONENT_MARKER_${index}__`
        componentMarkers.push({ marker, component: null, originalText: match[0] })
        index++
      }
    })

    // Video timestamp screenshot
    const videoTimestampMatches = Array.from(
      content.matchAll(/\[VIDEO_TIMESTAMP\](.*?)\[\/VIDEO_TIMESTAMP\]/gs)
    )
    videoTimestampMatches.forEach((match) => {
      try {
        const data = JSON.parse(match[1]) as unknown
        const marker = `__COMPONENT_MARKER_${index}__`
        const component = <VideoTimestamp key={`video-timestamp-${index}`} data={data} />
        componentMarkers.push({ marker, component, originalText: match[0] })
        index++
      } catch (e) {
        safeConsole.error('Failed to parse video timestamp data:', e)
        const marker = `__COMPONENT_MARKER_${index}__`
        componentMarkers.push({ marker, component: null, originalText: match[0] })
        index++
      }
    })

    // Code diff
    const codeDiffMatches = Array.from(content.matchAll(/\[CODE_DIFF\](.*?)\[\/CODE_DIFF\]/gs))
    codeDiffMatches.forEach((match) => {
      try {
        const data = JSON.parse(match[1])
        const marker = `__COMPONENT_MARKER_${index}__`
        const component = (
          <CodeDiff
            key={`code-diff-${index}`}
            beforeCode={data.beforeCode}
            afterCode={data.afterCode}
            language={data.language}
            title={data.title}
            className={data.className}
          />
        )
        componentMarkers.push({ marker, component, originalText: match[0] })
        index++
      } catch (e) {
        safeConsole.error('Failed to parse code diff data:', e)
      }
    })

    // Poll
    const pollMatches = Array.from(content.matchAll(/\[POLL\](.*?)\[\/POLL\]/gs))
    pollMatches.forEach((match) => {
      try {
        const data = JSON.parse(match[1])
        const marker = `__COMPONENT_MARKER_${index}__`
        const component = (
          <Poll
            key={`poll-${index}`}
            question={data.question}
            options={data.options}
            className={data.className}
          />
        )
        componentMarkers.push({ marker, component, originalText: match[0] })
        index++
      } catch (e) {
        safeConsole.error('Failed to parse poll data:', e)
      }
    })

    // Definition Card
    const definitionMatches = Array.from(content.matchAll(/\[DEFINITION\](.*?)\[\/DEFINITION\]/gs))
    definitionMatches.forEach((match) => {
      try {
        const fullContent = match[1]

        // Extract term and definition (always present)
        const mainParts = fullContent.split('[EXAMPLES]')[0].split('|')
        const term = mainParts[0].trim()
        const definition = mainParts[1]?.trim() || ''

        // Extract examples if present
        const examples: string[] = []
        const examplesMatch = fullContent.match(/\[EXAMPLES\](.*?)\[\/EXAMPLES\]/s)
        if (examplesMatch && examplesMatch[1]) {
          examples.push(...examplesMatch[1].split('|').map((ex) => ex.trim()))
        }

        // Extract related terms if present
        const relatedTerms: { term: string; link?: string }[] = []
        const relatedMatch = fullContent.match(/\[RELATED\](.*?)\[\/RELATED\]/s)
        if (relatedMatch && relatedMatch[1]) {
          relatedMatch[1].split('|').forEach((rel) => {
            const parts = rel.split('>')
            const relTerm = parts[0].trim()
            const link = parts.length > 1 ? parts[1].trim() : undefined
            relatedTerms.push({ term: relTerm, link })
          })
        }

        const marker = `__COMPONENT_MARKER_${index}__`
        const component = (
          <DefinitionCard
            key={`definition-${index}`}
            term={term}
            definition={definition}
            examples={examples}
            relatedTerms={relatedTerms}
          />
        )
        componentMarkers.push({ marker, component, originalText: match[0] })
        index++
      } catch (e) {
        safeConsole.error('Failed to parse definition data:', e)
      }
    })

    // Code Snippet
    const codeSnippetMatches = Array.from(
      content.matchAll(/\[CODE_SNIPPET\](.*?)\[\/CODE_SNIPPET\]/gs)
    )
    codeSnippetMatches.forEach((match) => {
      try {
        const data = JSON.parse(match[1])
        const marker = `__COMPONENT_MARKER_${index}__`
        const component = (
          <CodeSnippet
            key={`code-snippet-${index}`}
            code={data.code}
            language={data.language}
            title={data.title}
            description={data.description}
            canExecute={data.canExecute}
            className={data.className}
          />
        )
        componentMarkers.push({ marker, component, originalText: match[0] })
        index++
      } catch (e) {
        safeConsole.error('Failed to parse code snippet data:', e)
      }
    })

    // Rating Card
    const ratingMatches = Array.from(content.matchAll(/\[RATING\](.*?)\[\/RATING\]/gs))
    ratingMatches.forEach((match) => {
      try {
        const data = JSON.parse(match[1])
        const marker = `__COMPONENT_MARKER_${index}__`
        const component = (
          <RatingCard
            key={`rating-${index}`}
            title={data.title}
            description={data.description}
            scale={data.scale}
            maxRating={data.maxRating}
            className={data.className}
          />
        )
        componentMarkers.push({ marker, component, originalText: match[0] })
        index++
      } catch (e) {
        safeConsole.error('Failed to parse rating data:', e)
      }
    })

    // Progress Tracker
    const progressMatches = Array.from(content.matchAll(/\[PROGRESS\](.*?)\[\/PROGRESS\]/gs))
    progressMatches.forEach((match) => {
      try {
        const data = JSON.parse(match[1])
        const marker = `__COMPONENT_MARKER_${index}__`
        const component = (
          <ProgressTracker
            key={`progress-${index}`}
            title={data.title}
            description={data.description}
            steps={data.steps}
            currentValue={data.currentValue}
            maxValue={data.maxValue}
            showPercentage={data.showPercentage}
            className={data.className}
          />
        )
        componentMarkers.push({ marker, component, originalText: match[0] })
        index++
      } catch (e) {
        safeConsole.error('Failed to parse progress data:', e)
      }
    })

    // Tabbed Content
    const tabbedContentMatches = Array.from(content.matchAll(/\[TABS\](.*?)\[\/TABS\]/gs))
    tabbedContentMatches.forEach((match) => {
      try {
        const data = JSON.parse(match[1])
        const marker = `__COMPONENT_MARKER_${index}__`
        const component = (
          <TabbedContent
            key={`tabs-${index}`}
            title={data.title}
            tabs={data.tabs}
            defaultTabId={data.defaultTabId}
            className={data.className}
          />
        )
        componentMarkers.push({ marker, component, originalText: match[0] })
        index++
      } catch (e) {
        safeConsole.error('Failed to parse tabbed content data:', e)
      }
    })

    // Checklist (expandable to modal)
    const checklistMatches = Array.from(
      content.matchAll(/\[CHECKLIST\]([\s\S]*?)\[\/CHECKLIST\]/gi)
    )
    checklistMatches.forEach((match) => {
      try {
        const data = parseChecklistContent(match[1])

        const marker = `__COMPONENT_MARKER_${index}__`
        const component = (
          <ExpandableInteractiveWrapper
            key={`checklist-${index}`}
            title={data.title || 'Checklist'}
          >
            <Checklist title={data.title} items={data.items} className={data.className} />
          </ExpandableInteractiveWrapper>
        )
        componentMarkers.push({ marker, component, originalText: match[0] })
        index++
      } catch (e) {
        safeConsole.error('Failed to parse checklist data:', e)
      }
    })

    // Multi-step Form
    const formMatches = Array.from(content.matchAll(/\[FORM\](.*?)\[\/FORM\]/gs))
    formMatches.forEach((match) => {
      try {
        const data = JSON.parse(match[1])
        const marker = `__COMPONENT_MARKER_${index}__`
        const component = (
          <MultiStepForm
            key={`form-${index}`}
            title={data.title}
            steps={data.steps}
            onSubmit={(formData) => safeConsole.log('Form submitted:', formData)}
            className={data.className}
          />
        )
        componentMarkers.push({ marker, component, originalText: match[0] })
        index++
      } catch (e) {
        safeConsole.error('Failed to parse form data:', e)
      }
    })

    // Mind Map
    const mindMapMatches = Array.from(content.matchAll(/\[MIND_MAP\](.*?)\[\/MIND_MAP\]/gs))
    mindMapMatches.forEach((match) => {
      try {
        const data = JSON.parse(match[1])
        const marker = `__COMPONENT_MARKER_${index}__`
        const component = (
          <MindMap
            key={`mind-map-${index}`}
            title={data.title}
            rootNode={data.rootNode}
            className={data.className}
            layout={data.layout}
          />
        )
        componentMarkers.push({ marker, component, originalText: match[0] })
        index++
      } catch (e) {
        safeConsole.error('Failed to parse mind map data:', e)
      }
    })

    // Argument Map
    const argumentMapMatches = Array.from(
      content.matchAll(/\[ARGUMENT_MAP\](.*?)\[\/ARGUMENT_MAP\]/gs)
    )
    argumentMapMatches.forEach((match) => {
      try {
        const data = JSON.parse(match[1])
        const marker = `__COMPONENT_MARKER_${index}__`
        const component = (
          <ArgumentMap
            key={`argument-map-${index}`}
            title={data.title}
            rootNode={data.rootNode}
            className={data.className}
          />
        )
        componentMarkers.push({ marker, component, originalText: match[0] })
        index++
      } catch (e) {
        safeConsole.error('Failed to parse argument map data:', e)
      }
    })

    // Flow Chart
    const flowChartMatches = Array.from(content.matchAll(/\[FLOW_CHART\](.*?)\[\/FLOW_CHART\]/gs))
    flowChartMatches.forEach((match) => {
      try {
        const data = JSON.parse(match[1])
        const marker = `__COMPONENT_MARKER_${index}__`
        const component = (
          <FlowChart
            key={`flow-chart-${index}`}
            title={data.title}
            nodes={data.nodes}
            className={data.className}
            layout={data.layout}
            showLabels={data.showLabels}
            compact={data.compact}
          />
        )
        componentMarkers.push({ marker, component, originalText: match[0] })
        index++
      } catch (e) {
        safeConsole.error('Failed to parse flow chart data:', e)
      }
    })

    // Gantt Chart
    const ganttChartMatches = Array.from(
      content.matchAll(/\[GANTT_CHART\](.*?)\[\/GANTT_CHART\]/gs)
    )
    ganttChartMatches.forEach((match) => {
      try {
        const data = JSON.parse(match[1])
        const marker = `__COMPONENT_MARKER_${index}__`
        const component = (
          <GanttChart
            key={`gantt-chart-${index}`}
            title={data.title}
            tasks={data.tasks}
            className={data.className}
            startDate={data.startDate}
            endDate={data.endDate}
            showDependencies={data.showDependencies}
            showProgress={data.showProgress}
            compact={data.compact}
          />
        )
        componentMarkers.push({ marker, component, originalText: match[0] })
        index++
      } catch (e) {
        safeConsole.error('Failed to parse gantt chart data:', e)
      }
    })

    // Data Dashboard
    const dataDashboardMatches = Array.from(
      content.matchAll(/\[DATA_DASHBOARD\](.*?)\[\/DATA_DASHBOARD\]/gs)
    )
    dataDashboardMatches.forEach((match) => {
      try {
        const dashboardJson = match[1].trim()
        safeConsole.log('[DATA_DASHBOARD] Parsing JSON:', dashboardJson.substring(0, 100) + '...')

        if (!dashboardJson) {
          throw new Error('Empty dashboard data')
        }

        const data = JSON.parse(dashboardJson)

        if (!data.title) {
          safeConsole.warn('[DATA_DASHBOARD] Dashboard missing title, adding default')
          data.title = 'Data Dashboard'
        }

        // Handle sections-based format
        if (data.sections && Array.isArray(data.sections)) {
          safeConsole.log(
            '[DATA_DASHBOARD] Found sections format, transforming to compatible format'
          )

          if (!data.metrics) data.metrics = []
          if (!data.charts) data.charts = []

          // Define section type
          interface DashboardSection {
            title?: string
            description?: string
            metrics?: Array<any>
            charts?: Array<any>
            [key: string]: any
          }

          data.sections.forEach((section: DashboardSection) => {
            if (!section) return

            if (section.title) {
              data.metrics.push({
                label: section.title,
                value: section.description || '',
                status: 'neutral',
                type: 'section_header'
              })
            }

            if (section.metrics && Array.isArray(section.metrics)) {
              data.metrics = [...data.metrics, ...section.metrics]
            }

            if (section.charts && Array.isArray(section.charts)) {
              data.charts = [...data.charts, ...section.charts]
            }
          })
        }

        const marker = `__COMPONENT_MARKER_${index}__`
        const component = <DataDashboard key={`data-dashboard-${index}`} data={data} />
        componentMarkers.push({ marker, component, originalText: match[0] })
        index++
      } catch (e) {
        safeConsole.error('[DATA_DASHBOARD] Failed to parse JSON:', e)
        const marker = `__COMPONENT_MARKER_${index}__`
        const component = (
          <DataDashboard
            key={`data-dashboard-error-${index}`}
            data={{
              title: 'Dashboard Error',
              description: 'There was an error parsing the dashboard data. Please try again.',
              metrics: [
                {
                  label: 'Error',
                  value: e instanceof Error ? e.message : 'Unknown error',
                  status: 'negative'
                }
              ]
            }}
          />
        )
        componentMarkers.push({ marker, component, originalText: match[0] })
        index++
      }
    })

    // System Map
    const systemMapMatches = Array.from(content.matchAll(/\[SYSTEM_MAP\](.*?)\[\/SYSTEM_MAP\]/gs))
    systemMapMatches.forEach((match) => {
      try {
        const data = JSON.parse(match[1])
        const marker = `__COMPONENT_MARKER_${index}__`
        const component = <SystemMap key={`system-map-${index}`} data={data} />
        componentMarkers.push({ marker, component, originalText: match[0] })
        index++
      } catch (e) {
        safeConsole.error('Failed to parse system map data:', e)
      }
    })

    // Business Canvas
    const businessCanvasMatches = Array.from(
      content.matchAll(/\[BUSINESS_CANVAS\](.*?)\[\/BUSINESS_CANVAS\]/gs)
    )
    businessCanvasMatches.forEach((match) => {
      try {
        const rawData = JSON.parse(match[1])
        const normalizedData = { ...rawData }

        // Handle case where sections is an object instead of an array
        if (
          rawData.sections &&
          !Array.isArray(rawData.sections) &&
          typeof rawData.sections === 'object'
        ) {
          safeConsole.log('Converting business canvas object format to array format')

          // Convert object sections to array format
          normalizedData.sections = Object.entries(rawData.sections).map(([id, content]) => {
            // Determine section title based on id
            let title = id.replace(/([A-Z])/g, ' $1').trim() // Convert camelCase to title
            title = title.charAt(0).toUpperCase() + title.slice(1) // Capitalize first letter

            // Handle content - could be string or array
            const formattedContent = Array.isArray(content) ? content : [content]

            return {
              id,
              title,
              content: formattedContent,
              type: 'list'
            }
          })
        }

        const marker = `__COMPONENT_MARKER_${index}__`
        const component = <BusinessCanvas key={`business-canvas-${index}`} data={normalizedData} />
        componentMarkers.push({ marker, component, originalText: match[0] })
        index++
      } catch (e) {
        safeConsole.error('Failed to parse business canvas data:', e)
      }
    })

    // Citation Network, Stakeholder Map, Scenario Planner, and Market Positioner
    // These are processed separately after rendering the main content

    // Replace all component patterns with markers in the processed content
    componentMarkers.forEach(({ marker, originalText }) => {
      processedContent = processedContent.replace(originalText, marker)
    })

    const createAutoYoutubeMarker = (originalText: string, url: string, title?: string) => {
      const video = getAutoYoutubeVideo(url, title)
      if (!video) return originalText

      const { suffix } = getAutoYoutubeUrlParts(url)
      const marker = `__COMPONENT_MARKER_${index}__`
      const component = <YouTubeVisualizer key={`youtube-auto-video-${index}`} videos={[video]} />

      componentMarkers.push({ marker, component, originalText: '' })
      index++

      return `${marker}${suffix}`
    }

    const protectedYoutubeContent = protectMarkdownCodeSegments(processedContent)

    processedContent = protectedYoutubeContent.content.replace(
      YOUTUBE_MARKDOWN_LINK_REGEX,
      (originalText: string, title: string, url: string) =>
        createAutoYoutubeMarker(originalText, url, title)
    )
    processedContent = processedContent.replace(YOUTUBE_BARE_URL_REGEX, (url: string) =>
      createAutoYoutubeMarker(url, url)
    )
    processedContent = protectedYoutubeContent.restore(processedContent)

    // Inject skeleton placeholders ONLY while the assistant is still streaming.
    // After the stream ends, an unclosed `[TODO]` / `[CHECKLIST]` / etc. (or a
    // prose mention like "the `[TODO]` marker") must NOT swallow the rest of
    // the message into a permanent skeleton.
    if (isGenerating) {
    const partialTags = [
      'LOSI_APP_PREVIEW',
      'LOSI_PLANNER_DAY',
      'SPACE_ITEM_CARD',
      'VISUAL_ARTIFACT',
      'YOUTUBE_VIDEO',
      'YOUTUBE_VIDEO_LIST',
      'FLASHCARD',
      'TODO',
      'QUIZ',
      'TIMELINE',
      'STORYBOARD',
      'VIDEO_TIMESTAMP',
      'CITATION_NETWORK',
      'STAKEHOLDER_MAP',
      'SCENARIO_PLANNER',
      'MARKET_POSITIONER',
      'BUSINESS_CANVAS',
      'CHECKLIST',
      'MIND_MAP',
      'FLOW_CHART',
      'TABS',
      'DEFINITION',
      'POLL'
    ] as const
    partialTags.forEach((tag) => {
      const openTag = `[${tag}]`
      const closeTag = `[/${tag}]`
      let searchPos = 0
      while (true) {
        const pos = processedContent.indexOf(openTag, searchPos)
        if (pos === -1) break
        // If closing tag not found after this open tag, it's partial
        if (processedContent.indexOf(closeTag, pos + openTag.length) === -1) {
          // Prose false-positive: "the [TODO] marker" / inline code mentions.
          // Real streaming bodies start with list/json syntax or are still empty.
          const afterOpen = processedContent.slice(pos + openTag.length)
          const trimmedAfter = afterOpen.trimStart()
          const looksLikeProseMention =
            trimmedAfter.length > 0 &&
            !trimmedAfter.startsWith('{') &&
            !trimmedAfter.startsWith('[') &&
            !trimmedAfter.includes('|') &&
            /^[A-Za-z]/.test(trimmedAfter)
          if (looksLikeProseMention) {
            searchPos = pos + openTag.length
            continue
          }

          const marker = `__COMPONENT_MARKER_${index}__`

          // Choose the appropriate skeleton component based on tag type
          let skeletonComponent: React.ReactNode

          switch (tag) {
            case 'LOSI_APP_PREVIEW':
              skeletonComponent = (
                <div key={`skeleton-${index}`} className="my-3 animate-pulse">
                  <div className="rounded-xl border bg-background p-4 shadow-sm">
                    <div className="flex items-start gap-3">
                      <div className="size-11 rounded-lg bg-muted"></div>
                      <div className="min-w-0 flex-1 space-y-2">
                        <div className="h-4 w-1/2 rounded bg-muted"></div>
                        <div className="h-3 w-4/5 rounded bg-muted"></div>
                        <div className="h-3 w-2/3 rounded bg-muted"></div>
                      </div>
                    </div>
                  </div>
                </div>
              )
              break
            case 'LOSI_PLANNER_DAY':
              skeletonComponent = (
                <div key={`skeleton-${index}`} className="my-3 animate-pulse">
                  <div className="overflow-hidden rounded-2xl border bg-background">
                    <div className="flex items-center justify-between border-b p-4">
                      <div className="space-y-2">
                        <div className="h-4 w-36 rounded bg-muted" />
                        <div className="h-3 w-24 rounded bg-muted" />
                      </div>
                      <div className="h-7 w-20 rounded-full bg-muted" />
                    </div>
                    <div className="grid grid-cols-[64px_minmax(0,1fr)]">
                      <div className="space-y-4 border-r p-2">
                        <div className="h-3 w-9 rounded bg-muted" />
                        <div className="h-3 w-9 rounded bg-muted" />
                        <div className="h-3 w-9 rounded bg-muted" />
                      </div>
                      <div className="space-y-3 p-3">
                        <div className="ml-16 h-14 w-2/3 rounded-xl bg-muted" />
                        <div className="ml-4 h-16 w-1/2 rounded-xl bg-muted" />
                        <div className="ml-28 h-12 w-1/3 rounded-xl bg-muted" />
                      </div>
                    </div>
                  </div>
                </div>
              )
              break
            case 'SPACE_ITEM_CARD':
              // Keep the skeleton inline (no COMPONENT_MARKER) so a streaming
              // card inside a markdown table doesn't tear the row apart.
              skeletonComponent = null
              processedContent = `${processedContent.slice(0, pos)}<space-item-card data-payload="" data-loading="1"></space-item-card>`
              index++
              searchPos = pos + '<space-item-card data-payload="" data-loading="1"></space-item-card>'.length
              // Skip the default marker push below
              continue
            case 'FLASHCARD':
              skeletonComponent = <FlashcardSkeleton key={`skeleton-${index}`} />
              break
            case 'TODO':
              skeletonComponent = <TodoListSkeleton key={`skeleton-${index}`} />
              break
            case 'QUIZ':
              skeletonComponent = <QuizQuestionSkeleton key={`skeleton-${index}`} />
              break
            case 'TIMELINE':
              skeletonComponent = <TimelineSkeleton key={`skeleton-${index}`} />
              break
            case 'STORYBOARD':
            case 'VIDEO_TIMESTAMP':
            case 'YOUTUBE_VIDEO':
            case 'YOUTUBE_VIDEO_LIST':
              skeletonComponent = (
                <div key={`skeleton-${index}`} className="my-4 animate-pulse space-y-3">
                  <div className="aspect-video rounded-lg bg-muted"></div>
                  <div className="h-3 w-2/3 rounded bg-muted"></div>
                  <div className="h-3 w-1/2 rounded bg-muted"></div>
                </div>
              )
              break
            case 'BUSINESS_CANVAS':
              skeletonComponent = <BusinessCanvasSkeleton key={`skeleton-${index}`} />
              break
            case 'CITATION_NETWORK':
              skeletonComponent = <CitationNetworkSkeleton key={`skeleton-${index}`} />
              break
            case 'STAKEHOLDER_MAP':
              skeletonComponent = <StakeholderMapSkeleton key={`skeleton-${index}`} />
              break
            case 'SCENARIO_PLANNER':
              skeletonComponent = <ScenarioPlannerSkeleton key={`skeleton-${index}`} />
              break
            case 'MARKET_POSITIONER':
              skeletonComponent = <MarketPositionerSkeleton key={`skeleton-${index}`} />
              break
            case 'CHECKLIST':
              skeletonComponent = <ChecklistSkeleton key={`skeleton-${index}`} />
              break
            case 'MIND_MAP':
              skeletonComponent = <MindMapSkeleton key={`skeleton-${index}`} />
              break
            case 'FLOW_CHART':
              skeletonComponent = <FlowChartSkeleton key={`skeleton-${index}`} />
              break
            case 'TABS':
              skeletonComponent = <TabbedContentSkeleton key={`skeleton-${index}`} />
              break
            case 'DEFINITION':
              skeletonComponent = <DefinitionCardSkeleton key={`skeleton-${index}`} />
              break
            case 'POLL':
              skeletonComponent = <PollSkeleton key={`skeleton-${index}`} />
              break
            default:
              // Generic skeleton for other component types
              skeletonComponent = (
                <div key={`skeleton-${index}`} className="my-4 animate-pulse space-y-2">
                  <div className="h-4 w-3/4 rounded bg-gray-200 dark:bg-gray-700"></div>
                  <div className="h-4 rounded bg-gray-200 dark:bg-gray-700"></div>
                  <div className="h-4 w-5/6 rounded bg-gray-200 dark:bg-gray-700"></div>
                </div>
              )
          }

          componentMarkers.push({
            marker,
            component: skeletonComponent,
            originalText: processedContent.slice(pos)
          })
          processedContent = `${processedContent.slice(0, pos)}${marker}`
          index++
          searchPos = pos + marker.length
        } else {
          searchPos = pos + openTag.length
        }
      }
    })
    }

    return { processedContent, componentMarkers }
  }

  // Process the message content
  const { processedContent, componentMarkers } = processContent(messageContent)

  // Split content by code blocks
  const contentParts = processedContent.split(/(\[CODE_BLOCK_\d+\])/s).filter(Boolean)

  // Prepare final rendered elements
  const renderedElements = contentParts.map((part, partIndex) => {
    const codeBlockMatch = part.match(/\[CODE_BLOCK_(\d+)\]/)

    if (codeBlockMatch) {
      const blockIndex = parseInt(codeBlockMatch[1])
      const block = codeBlocks?.[blockIndex]
      if (!block) {
        safeConsole.log('No code block found for index:', blockIndex)
        return null
      }

      safeConsole.log('MessageMarkdown: Processing code block:', {
        language: block.language,
        type: block.type,
        content: block.code?.substring(0, 100) + '...'
      })

      // Enhanced Mermaid detection
      const isMermaidContent = block.code
        ?.trim()
        .match(
          /^(graph|flowchart|sequenceDiagram|classDiagram|stateDiagram|erDiagram|journey|gantt|pie|mindmap|timeline|gitGraph)\s/i
        )
      const isMermaidFence = block.code?.trim().toLowerCase().startsWith('```mermaid')
      const isMermaid =
        block.type === 'mermaid' ||
        block.language?.toLowerCase() === 'mermaid' ||
        isMermaidContent ||
        isMermaidFence

      if (isMermaid) {
        safeConsole.log('MessageMarkdown: Rendering Mermaid diagram:', {
          type: block.type,
          language: block.language,
          isMermaidContent: !!isMermaidContent,
          isMermaidFence,
          contentPreview: block.code?.substring(0, 100)
        })
        let cleanContent = block.code || ''
        return (
          <MermaidComponent
            key={`mermaid-${partIndex}`}
            chatId={outcomeChatId}
            content={cleanContent}
            isStreaming={!!isGenerating}
            messageId={outcomeMessageId}
            shouldTrackOutcome={shouldTrackChatOutputOutcome}
          />
        )
      }

      const jsxPreview = getJsxPreviewData(block.code || '', block.language || '')
      if (jsxPreview) {
        return (
          <GeneratedJSXPreview
            bindings={jsxPreview.bindings}
            isStreaming={!!isGenerating}
            jsx={jsxPreview.jsx}
            key={`jsx-preview-${partIndex}`}
          />
        )
      }

      if (isOpenUIFenceLanguage(block.language || '')) {
        return (
          <OpenUIBlock
            key={`openui-${partIndex}`}
            code={block.code || ''}
            isStreaming={!!isGenerating}
            onActionPrompt={onInteractiveSubmit}
          />
        )
      }

      const htmlCard = renderHtmlCardFromFence(
        block.code || '',
        block.language || '',
        `${messageId || 'msg'}-${partIndex}`,
        onInteractiveSubmit,
        {
          isStreaming: !!isGenerating,
          states: htmlCardStates,
          onStateChange: handleHtmlCardStateChange
        }
      )
      if (htmlCard) return htmlCard

      if (experimental_code_editor && !!block.filename) {
        return (
          <CodeBlockButton
            loading={!!isGenerating}
            key={`code-block-${partIndex}`}
            {...block}
            onClick={() => handleCodeBlockClick(block)}
          />
        )
      } else {
        return (
          <CodeViewer
            isEditable={false}
            onCodeChange={() => {}}
            key={`code-block-${partIndex}`}
            codeBlock={block}
            isGenerating={isGenerating}
            autoScroll={false}
          />
        )
      }
    }

    // Process part to replace component markers with actual components
    let processedPart = part

    // Create an array to hold both markdown and component elements for this part
    const mixedContent: React.ReactNode[] = []

    // Split by component markers
    const partSegments = processedPart.split(/(__COMPONENT_MARKER_\d+__)/)

    partSegments.forEach((segment, segIndex) => {
      const markerMatch = segment.match(/__COMPONENT_MARKER_(\d+)__/)

      if (markerMatch) {
        const markerIndex = parseInt(markerMatch[1])
        const marker = componentMarkers.find((m) => m.marker === segment)

        if (marker) {
          // Add the component directly to our mixed content array
          mixedContent.push(marker.component)
        } else {
          // If marker not found, just render the text
          mixedContent.push(
            <MessageMarkdownMemoized
              key={`markdown-marker-missing-${shouldAnimateMarkdown ? 'stream' : 'static'}-${partIndex}-${segIndex}`}
              className={cn(
                MESSAGE_MARKDOWN_PROSE_CLASSNAME,
                shouldAnimateMarkdown ? 'generating' : ''
              )}
              {...(shouldAnimateMarkdown ? ACTIVE_STREAMING_MARKDOWN_PROPS : {})}
              remarkPlugins={MESSAGE_MARKDOWN_REMARK_PLUGINS}
              plugins={streamdownMathPlugins}
              urlTransform={urlTransform}
              allowedTags={MESSAGE_MARKDOWN_ALLOWED_TAGS}
              components={{
                ...messageMarkdownCustomComponents,
                // ... existing components ...
                a({ children, ...props }) {
                  const citationNumber = getCitationNumber(children)
                  if (citationNumber !== null) {
                    const citationIndex = citationNumber - 1
                    const citationCount = citationSources.length
                    const canScrollToSource =
                      messageId && citationIndex >= 0 && citationIndex < citationCount

                    return (
                      <CitationChip
                        href={props.href}
                        citationNumber={citationNumber}
                        source={citationSources[citationIndex] || null}
                        scrollTargetId={
                          canScrollToSource ? `citation-${messageId}-${citationIndex}` : null
                        }
                      />
                    )
                  }

                  // Detect presentation links (PowerPoint files)
                  const href = props.href || ''
                  if (href) {
                    // Detect presentation download URLs
                    const isPresentationUrl =
                      /\blos[iy]?_presentation_\d+\.pptx$/.test(href) ||
                      /presentation_\d+\.pptx.*download/.test(href)
                    if (isPresentationUrl) {
                      return (
                        <PresentationLinkPreview
                          chatId={outcomeChatId}
                          url={href}
                          title="Losi AI Presentation"
                          fileName={href.split('/').pop() || 'presentation.pptx'}
                          messageId={outcomeMessageId}
                          shouldTrackOutcome={shouldTrackChatOutputOutcome}
                        />
                      )
                    }

                    // Detect extension connection URLs
                    const isConnectUrl = /^connect:([a-zA-Z0-9]+)$/.test(href)
                    const isExtensionConnectUrl = /\/extensions\/connect\/[a-zA-Z0-9]+/.test(href)
                    const isComposioAuthUrl =
                      href.includes('backend.composio.dev') ||
                      href.includes('accounts.composio.dev') ||
                      href.includes('connect.composio.dev') ||
                      href.includes('connected_accounts/link') ||
                      href.includes('/api/v3/connected_accounts')
                    if (isConnectUrl || isExtensionConnectUrl || isComposioAuthUrl) {
                      return <ExtensionConnectionBlock url={href} />
                    }

                    // Detect audio URLs
                    const audioExt = href.split('.').pop()?.toLowerCase()
                    const isAudioUrl =
                      /\.(mp3|wav|m4a|flac|ogg)(\?|$)/i.test(href) ||
                      ['mp3', 'wav', 'm4a', 'flac', 'ogg'].includes(audioExt || '')
                    if (isAudioUrl) {
                      return (
                        <AnimatedMarkdownMusic
                          src={href}
                          alt={typeof children === 'string' ? children : 'audio'}
                          isStreaming={!!isGenerating}
                        />
                      )
                    }

                    // Detect 3D model URLs (glb/gltf/ply)
                    const modelExt = href.split('.').pop()?.toLowerCase()
                    const isModelUrl =
                      /\.(glb|gltf|ply)(\?|$)/i.test(href) ||
                      ['glb', 'gltf', 'ply'].includes(modelExt || '')
                    if (isModelUrl) {
                      return (
                        <AnimatedMarkdown3D
                          src={href}
                          alt={typeof children === 'string' ? children : '3D asset'}
                          isStreaming={!!isGenerating}
                          chatId={outcomeChatId}
                          messageId={outcomeMessageId}
                          shouldTrackOutcome={shouldTrackChatOutputOutcome}
                        />
                      )
                    }
                    // Check if it's a video link
                    const fileExtension = href.split('.').pop()?.toLowerCase()
                    const hasVideoExtension =
                      fileExtension === 'mp4' || fileExtension === 'webm' || fileExtension === 'mov'
                    const isGif = fileExtension === 'gif' || href.match(/\.gif(\?|$)/)
                    const isManimLink =
                      href.includes('anim.losi.one') ||
                      href.includes('mapi.losi.one') ||
                      (typeof children === 'string' &&
                        (children.toLowerCase().includes('manim') ||
                          children.toLowerCase().includes('math animation')))
                    const hasVideoIndicator =
                      href.includes('video') || href.match(/\.(mp4|webm|mov)(\?|$)/)

                    const isVideoUrl =
                      hasVideoExtension || isManimLink || hasVideoIndicator || isGif

                    if (isVideoUrl) {
                      return (
                        <AnimatedMarkdownVideo
                          src={href}
                          alt={typeof children === 'string' ? children : 'video'}
                          isStreaming={!!isGenerating}
                        />
                      )
                    }
                  }

                  // Detect and render Twitter/X status embeds
                  const isTwitterStatus =
                    /https?:\/\/(twitter\.com|x\.com)\/[^\/]+\/status\/\d+/.test(href)
                  if (isTwitterStatus) {
                    return <TwitterEmbed url={href} />
                  }

                  // CRM mention: [lead: Ayo Elutilo](lead:uuid) -> render as chip
                  const crmMentionMatch = href.match(/^(lead|contact|company):([a-f0-9-]+)$/i)
                  if (crmMentionMatch) {
                    const type = crmMentionMatch[1].toLowerCase() as 'lead' | 'contact' | 'company'
                    const id = crmMentionMatch[2]
                    const label =
                      typeof children === 'string'
                        ? children.replace(new RegExp(`^${type}:\\s*`, 'i'), '').trim() || children
                        : String(children)
                    return (
                      <CrmMentionChip
                        type={type}
                        label={label}
                        id={id}
                        compact
                        className="align-middle"
                      />
                    )
                  }

                  const spaceMemberMentionMatch = href.match(/^space-member:([a-z0-9-]+)$/i)
                  if (spaceMemberMentionMatch) {
                    const label =
                      typeof children === 'string'
                        ? children.trim() || 'teammate'
                        : String(children)
                    return (
                      <SpaceMemberMentionChip
                        label={label}
                        userId={spaceMemberMentionMatch[1]}
                        className="align-middle"
                      />
                    )
                  }

                  // Chat mentions (@[Name](member:…) / assistant / everyone / losi …)
                  const chatMentionMatch = href.match(
                    /^(member|assistant|everyone|losi|staffing[-_]agent|super-agent|space_member):(.+)$/i
                  )
                  if (chatMentionMatch) {
                    const label =
                      typeof children === 'string'
                        ? children.trim() || 'teammate'
                        : String(children).trim()
                    return (
                      <SpaceMemberMentionChip
                        label={label.replace(/^@+/, '')}
                        userId={chatMentionMatch[2]}
                        className="align-middle"
                      />
                    )
                  }

                  const spaceItem = parseSidekickSpaceItemMarkdownHref(
                    href,
                    getReactNodeText(children)
                  )
                  if (spaceItem) {
                    return (
                      <SpaceItemMessageCard
                        className="not-prose"
                        href={getSidekickSpaceItemHref(spaceItem)}
                        item={spaceItem}
                        onOpen={openSpaceItemFromMessage}
                      />
                    )
                  }

                  const spaceItemLinkProps = getSpaceItemLinkProps(
                    props.href,
                    getReactNodeText(children)
                  )

                  if (spaceItemLinkProps) {
                    return (
                      <a {...props} {...spaceItemLinkProps}>
                        {children}
                      </a>
                    )
                  }

                  const inAppLinkProps = getInAppLinkProps(props.href)

                  if (inAppLinkProps) {
                    return (
                      <a {...props} {...inAppLinkProps}>
                        {children}
                      </a>
                    )
                  }

                  return (
                    <a {...props} target="_blank" rel={getMessageLinkRel(props.rel)}>
                      {children}
                    </a>
                  )
                },
                p({ children }) {
                  return <p className="mb-2 whitespace-pre-wrap last:mb-0">{children}</p>
                },
                ul({ children, className }) {
                  const isTaskList = className?.includes('contains-task-list')
                  return (
                    <ul
                      className={cn(
                        isTaskList
                          ? 'my-3 list-none space-y-1 pl-0'
                          : 'my-3 list-disc space-y-1 pl-6 marker:text-foreground/70',
                        className
                      )}
                    >
                      {children}
                    </ul>
                  )
                },
                ol({ children, className }) {
                  return (
                    <ol
                      className={cn(
                        'my-3 list-decimal space-y-1 pl-6 marker:text-foreground/70',
                        className
                      )}
                    >
                      {children}
                    </ol>
                  )
                },
                li({ children, className }) {
                  const isTaskItem = className?.includes('task-list-item')
                  return (
                    <li
                      className={cn(
                        isTaskItem
                          ? 'my-1 flex list-none items-start gap-2 pl-0 [&>p]:my-0'
                          : 'my-1 [&>p]:my-0 [&>p]:inline',
                        className
                      )}
                    >
                      {children}
                    </li>
                  )
                },
                input({ type, checked, disabled, ...props }) {
                  if (type === 'checkbox') {
                    return (
                      <input
                        type="checkbox"
                        checked={Boolean(checked)}
                        disabled={disabled ?? true}
                        readOnly
                        aria-label="Checklist item"
                        className="mt-1 size-4 shrink-0 rounded border border-input"
                        {...props}
                      />
                    )
                  }

                  return <input type={type} disabled={disabled} readOnly {...props} />
                },
                pre({ children, className, ...props }) {
                  const match = /language-([^\s]+)/.exec(className || '')
                  const language = match ? match[1].toLowerCase() : ''
                  const content = React.Children.toArray(children)
                    .filter((child) => React.isValidElement(child) && child.type === 'code')
                    .map((child) => (React.isValidElement(child) ? child.props.children : ''))
                    .join('')
                  const isMermaidContent = content
                    .trim()
                    .match(
                      /^(graph|flowchart|sequenceDiagram|classDiagram|stateDiagram|erDiagram|journey|gantt|pie|mindmap|timeline|gitGraph)\s/i
                    )
                  const isMermaidFence = content.trim().toLowerCase().startsWith('```mermaid')
                  const isMermaid = language === 'mermaid' || isMermaidContent || isMermaidFence
                  const jsxPreview = getJsxPreviewData(content, language)

                  if (jsxPreview) {
                    return (
                      <GeneratedJSXPreview
                        bindings={jsxPreview.bindings}
                        isStreaming={!!isGenerating}
                        jsx={jsxPreview.jsx}
                      />
                    )
                  }

                  if (isOpenUIFenceLanguage(language)) {
                    return (
                      <OpenUIBlock
                        code={content}
                        isStreaming={!!isGenerating}
                        onActionPrompt={onInteractiveSubmit}
                      />
                    )
                  }

                  const htmlCard = renderHtmlCardFromFence(
                    content,
                    language,
                    `${messageId || 'msg'}-pre-${language}`,
                    onInteractiveSubmit,
                    {
                      isStreaming: !!isGenerating,
                      states: htmlCardStates,
                      onStateChange: handleHtmlCardStateChange
                    }
                  )
                  if (htmlCard) return htmlCard

                  if (isMermaid) {
                    safeConsole.log('MessageMarkdown: Found Mermaid content in pre')
                    let cleanContent = content
                    if (isMermaidFence) {
                      cleanContent = content
                        .replace(/^```mermaid\n?/, '')
                        .replace(/```$/, '')
                        .trim()
                    }
                    return (
                      <MermaidComponent
                        chatId={outcomeChatId}
                        content={cleanContent}
                        isStreaming={!!isGenerating}
                        messageId={outcomeMessageId}
                        shouldTrackOutcome={shouldTrackChatOutputOutcome}
                      />
                    )
                  }
                  return (
                    <pre className={className} {...props}>
                      {children}
                    </pre>
                  )
                },
                code({
                  className,
                  children,
                  ...props
                }: ComponentPropsWithoutRef<'code'> & { 'data-inline'?: boolean }) {
                  const match = /language-([^\s]+)/.exec(className || '')
                  const language = match ? match[1].toLowerCase() : ''
                  const content = children?.toString() || ''
                  const isMermaidContent = content
                    .trim()
                    .match(
                      /^(graph|flowchart|sequenceDiagram|classDiagram|stateDiagram|erDiagram|journey|gantt|pie|mindmap|timeline|gitGraph)\s/i
                    )
                  const isMermaidFence = content.trim().toLowerCase().startsWith('```mermaid')
                  const isMermaid = language === 'mermaid' || isMermaidContent || isMermaidFence

                  if (!props['data-inline'] && isMermaid) {
                    safeConsole.log('MessageMarkdown: Skipping code block for Mermaid content')
                    return null // The diagram will be handled by the pre component
                  }
                  return (
                    <code className={className} {...props}>
                      {children}
                    </code>
                  )
                }
              }}
            >
              {segment}
            </MessageMarkdownMemoized>
          )
        }
      } else if (segment.trim()) {
        // Regular text - render it as markdown
        mixedContent.push(
          <MessageMarkdownMemoized
            key={`markdown-${shouldAnimateMarkdown ? 'stream' : 'static'}-${partIndex}-${segIndex}`}
            className={cn(
              MESSAGE_MARKDOWN_PROSE_CLASSNAME,
              shouldAnimateMarkdown ? 'generating' : ''
            )}
            {...(shouldAnimateMarkdown ? ACTIVE_STREAMING_MARKDOWN_PROPS : {})}
            remarkPlugins={MESSAGE_MARKDOWN_REMARK_PLUGINS}
            plugins={streamdownMathPlugins}
            urlTransform={urlTransform}
            allowedTags={MESSAGE_MARKDOWN_ALLOWED_TAGS}
            components={{
              ...messageMarkdownCustomComponents,
              // ... same component definitions as above ...
              a({ children, ...props }) {
                const citationNumber = getCitationNumber(children)
                if (citationNumber !== null) {
                  const citationIndex = citationNumber - 1
                  const citationCount = citationSources.length
                  const canScrollToSource =
                    messageId && citationIndex >= 0 && citationIndex < citationCount

                  return (
                    <CitationChip
                      href={props.href}
                      citationNumber={citationNumber}
                      source={citationSources[citationIndex] || null}
                      scrollTargetId={
                        canScrollToSource ? `citation-${messageId}-${citationIndex}` : null
                      }
                    />
                  )
                }

                // Detect presentation links (PowerPoint files)
                const href = props.href || ''
                if (href) {
                  // Detect presentation download URLs
                  const isPresentationUrl =
                    /\blos[iy]?_presentation_\d+\.pptx$/.test(href) ||
                    /presentation_\d+\.pptx.*download/.test(href)
                  if (isPresentationUrl) {
                    return (
                      <PresentationLinkPreview
                        chatId={outcomeChatId}
                        url={href}
                        title="Losi AI Presentation"
                        fileName={href.split('/').pop() || 'presentation.pptx'}
                        messageId={outcomeMessageId}
                        shouldTrackOutcome={shouldTrackChatOutputOutcome}
                      />
                    )
                  }

                  // Detect extension connection URLs
                  const isConnectUrl = /^connect:([a-zA-Z0-9]+)$/.test(href)
                  const isExtensionConnectUrl = /\/extensions\/connect\/[a-zA-Z0-9]+/.test(href)
                  const isComposioAuthUrl =
                    href.includes('backend.composio.dev') ||
                    href.includes('accounts.composio.dev') ||
                    href.includes('connect.composio.dev') ||
                    href.includes('connected_accounts/link') ||
                    href.includes('/api/v3/connected_accounts')
                  if (isConnectUrl || isExtensionConnectUrl || isComposioAuthUrl) {
                    return <ExtensionConnectionBlock url={href} />
                  }

                  // Detect audio URLs
                  const audioExt = href.split('.').pop()?.toLowerCase()
                  const isAudioUrl =
                    /\.(mp3|wav|m4a|flac|ogg)(\?|$)/i.test(href) ||
                    ['mp3', 'wav', 'm4a', 'flac', 'ogg'].includes(audioExt || '')
                  if (isAudioUrl) {
                    return (
                      <AnimatedMarkdownMusic
                        src={href}
                        alt={typeof children === 'string' ? children : 'audio'}
                        isStreaming={!!isGenerating}
                      />
                    )
                  }

                  // Detect 3D model URLs (glb/gltf/ply)
                  const modelExt = href.split('.').pop()?.toLowerCase()
                  const isModelUrl =
                    /\.(glb|gltf|ply)(\?|$)/i.test(href) ||
                    ['glb', 'gltf', 'ply'].includes(modelExt || '')
                  if (isModelUrl) {
                    return (
                      <AnimatedMarkdown3D
                        src={href}
                        alt={typeof children === 'string' ? children : '3D asset'}
                        isStreaming={!!isGenerating}
                        chatId={outcomeChatId}
                        messageId={outcomeMessageId}
                        shouldTrackOutcome={shouldTrackChatOutputOutcome}
                      />
                    )
                  }
                  // Check if it's a video link
                  const fileExtension = href.split('.').pop()?.toLowerCase()
                  const hasVideoExtension =
                    fileExtension === 'mp4' || fileExtension === 'webm' || fileExtension === 'mov'
                  const isGif = fileExtension === 'gif' || href.match(/\.gif(\?|$)/)
                  const isManimLink =
                    href.includes('anim.losi.one') ||
                    href.includes('mapi.losi.one') ||
                    (typeof children === 'string' &&
                      (children.toLowerCase().includes('manim') ||
                        children.toLowerCase().includes('math animation')))
                  const hasVideoIndicator =
                    href.includes('video') || href.match(/\.(mp4|webm|mov)(\?|$)/)

                  const isVideoUrl = hasVideoExtension || isManimLink || hasVideoIndicator || isGif

                  if (isVideoUrl) {
                    return (
                      <AnimatedMarkdownVideo
                        src={href}
                        alt={typeof children === 'string' ? children : 'video'}
                        isStreaming={!!isGenerating}
                      />
                    )
                  }
                }

                // Detect and render Twitter/X status embeds
                const isTwitterStatus =
                  /https?:\/\/(twitter\.com|x\.com)\/[^\/]+\/status\/\d+/.test(href)
                if (isTwitterStatus) {
                  return <TwitterEmbed url={href} />
                }

                const crmMentionMatch = href.match(/^(lead|contact|company):([a-f0-9-]+)$/i)
                if (crmMentionMatch) {
                  const type = crmMentionMatch[1].toLowerCase() as 'lead' | 'contact' | 'company'
                  const id = crmMentionMatch[2]
                  const label =
                    typeof children === 'string'
                      ? children.replace(new RegExp(`^${type}:\\s*`, 'i'), '').trim() || children
                      : String(children)
                  return (
                    <CrmMentionChip
                      type={type}
                      label={label}
                      id={id}
                      compact
                      className="align-middle"
                    />
                  )
                }

                const spaceMemberMentionMatch = href.match(/^space-member:([a-z0-9-]+)$/i)
                if (spaceMemberMentionMatch) {
                  const label =
                    typeof children === 'string' ? children.trim() || 'teammate' : String(children)
                  return (
                    <SpaceMemberMentionChip
                      label={label}
                      userId={spaceMemberMentionMatch[1]}
                      className="align-middle"
                    />
                  )
                }

                // Chat mentions (@[Name](member:…) / assistant / everyone / losi …)
                const chatMentionMatch = href.match(
                  /^(member|assistant|everyone|losi|staffing[-_]agent|super-agent|space_member):(.+)$/i
                )
                if (chatMentionMatch) {
                  const label =
                    typeof children === 'string'
                      ? children.trim() || 'teammate'
                      : String(children).trim()
                  return (
                    <SpaceMemberMentionChip
                      label={label.replace(/^@+/, '')}
                      userId={chatMentionMatch[2]}
                      className="align-middle"
                    />
                  )
                }

                const spaceItem = parseSidekickSpaceItemMarkdownHref(
                  href,
                  getReactNodeText(children)
                )
                if (spaceItem) {
                  return (
                    <SpaceItemMessageCard
                      className="not-prose"
                      href={getSidekickSpaceItemHref(spaceItem)}
                      item={spaceItem}
                      onOpen={openSpaceItemFromMessage}
                    />
                  )
                }

                const isLikelyCitationBadge = (() => {
                  if (!children) return false
                  const text =
                    typeof children === 'string'
                      ? children
                      : Array.isArray(children) &&
                          children.length === 1 &&
                          typeof children[0] === 'string'
                        ? children[0]
                        : null
                  if (!text) return false
                  const trimmed = text.trim()
                  // Matches "1", "1.", "[1]", "[1]."
                  return /^\[?\d+\]?\.*$/.test(trimmed)
                })()

                if (isLikelyCitationBadge) {
                  const rawText =
                    typeof children === 'string'
                      ? children
                      : Array.isArray(children) &&
                          children.length === 1 &&
                          typeof children[0] === 'string'
                        ? children[0]
                        : ''
                  const label = rawText
                    .trim()
                    .replace(/^\[/, '')
                    .replace(/\]$/, '')
                    .replace(/\.$/, '')
                  const citationInAppLinkProps = getInAppLinkProps(props.href)

                  return (
                    <a
                      {...props}
                      {...(citationInAppLinkProps ?? {
                        target: '_blank',
                        rel: getMessageLinkRel(props.rel)
                      })}
                      className="ml-1 inline-flex size-5 -translate-y-px items-center justify-center rounded-full border border-primary/30 bg-primary/15 align-super text-[11px] font-semibold leading-none text-primary no-underline transition-colors hover:border-primary/45 hover:bg-primary/25 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                      aria-label={`Source ${label}`}
                    >
                      {label}
                    </a>
                  )
                }

                const spaceItemLinkProps = getSpaceItemLinkProps(
                  props.href,
                  getReactNodeText(children)
                )

                if (spaceItemLinkProps) {
                  return (
                    <a {...props} {...spaceItemLinkProps}>
                      {children}
                    </a>
                  )
                }

                const inAppLinkProps = getInAppLinkProps(props.href)

                if (inAppLinkProps) {
                  return (
                    <a {...props} {...inAppLinkProps}>
                      {children}
                    </a>
                  )
                }

                return (
                  <a {...props} target="_blank" rel={getMessageLinkRel(props.rel)}>
                    {children}
                  </a>
                )
              },
              p({ children }) {
                return <p className="mb-2 whitespace-pre-wrap last:mb-0">{children}</p>
              },
              ul({ children, className }) {
                const isTaskList = className?.includes('contains-task-list')
                return (
                  <ul
                    className={cn(
                      isTaskList
                        ? 'my-3 list-none space-y-1 pl-0'
                        : 'my-3 list-disc space-y-1 pl-6 marker:text-foreground/70',
                      className
                    )}
                  >
                    {children}
                  </ul>
                )
              },
              ol({ children, className }) {
                return (
                  <ol
                    className={cn(
                      'my-3 list-decimal space-y-1 pl-6 marker:text-foreground/70',
                      className
                    )}
                  >
                    {children}
                  </ol>
                )
              },
              li({ children, className }) {
                const isTaskItem = className?.includes('task-list-item')
                return (
                  <li
                    className={cn(
                      isTaskItem
                        ? 'my-1 flex list-none items-start gap-2 pl-0 [&>p]:my-0'
                        : 'my-1 [&>p]:my-0 [&>p]:inline',
                      className
                    )}
                  >
                    {children}
                  </li>
                )
              },
              input({ type, checked, disabled, ...props }) {
                if (type === 'checkbox') {
                  return (
                    <input
                      type="checkbox"
                      checked={Boolean(checked)}
                      disabled={disabled ?? true}
                      readOnly
                      aria-label="Checklist item"
                      className="mt-1 size-4 shrink-0 rounded border border-input"
                      {...props}
                    />
                  )
                }

                return <input type={type} disabled={disabled} readOnly {...props} />
              },
              img({ src, ...props }) {
                if (!src) return null

                const imageAlt = typeof props.alt === 'string' ? props.alt : 'image'
                // Enhanced video URL detection
                const fileExtension = src.split('.').pop()?.toLowerCase()
                const hasVideoExtension =
                  fileExtension === 'mp4' || fileExtension === 'webm' || fileExtension === 'mov'
                const isGif = fileExtension === 'gif' || src.match(/\.gif(\?|$)/)
                const isReplicateUrl = src.includes('replicate.delivery')
                const hasVideoIndicator =
                  src.includes('video') || src.match(/\.(mp4|webm|mov)(\?|$)/)
                const altIndicatesVideo =
                  imageAlt.toLowerCase().includes('video') ||
                  imageAlt.toLowerCase().includes('generated video')
                const isVideoUrl =
                  hasVideoExtension ||
                  (isReplicateUrl && altIndicatesVideo) ||
                  hasVideoIndicator ||
                  isGif

                if (isVideoUrl) {
                  return (
                    <AnimatedMarkdownVideo src={src} alt={imageAlt} isStreaming={!!isGenerating} />
                  )
                }

                // Audio inline rendering for image-tagged links
                const isAudioUrl = /\.(mp3|wav|m4a|flac|ogg)(\?|$)/i.test(src)
                if (isAudioUrl) {
                  return (
                    <AnimatedMarkdownMusic src={src} alt={imageAlt} isStreaming={!!isGenerating} />
                  )
                }

                // 3D model inline rendering
                const isModelUrl = /\.(glb|gltf|ply)(\?|$)/i.test(src)
                if (isModelUrl) {
                  return (
                    <AnimatedMarkdown3D
                      src={src}
                      alt={imageAlt}
                      isStreaming={!!isGenerating}
                      chatId={outcomeChatId}
                      messageId={outcomeMessageId}
                      shouldTrackOutcome={shouldTrackChatOutputOutcome}
                    />
                  )
                }

                // Otherwise render as image
                const imageSrc = getSafeRenderableImageUrl(src)
                if (!imageSrc) return null

                return (
                  <ImageWithPreview
                    src={imageSrc}
                    alt={imageAlt}
                    isStreaming={!!isGenerating}
                    className="my-0 h-auto max-w-full cursor-pointer rounded-lg object-contain"
                  />
                )
              },
              pre({ children, className, ...props }) {
                const match = /language-([^\s]+)/.exec(className || '')
                const language = match ? match[1].toLowerCase() : ''
                const content = React.Children.toArray(children)
                  .filter((child) => React.isValidElement(child) && child.type === 'code')
                  .map((child) => (React.isValidElement(child) ? child.props.children : ''))
                  .join('')
                const isMermaidContent = content
                  .trim()
                  .match(
                    /^(graph|flowchart|sequenceDiagram|classDiagram|stateDiagram|erDiagram|journey|gantt|pie|mindmap|timeline|gitGraph)\s/i
                  )
                const isMermaidFence = content.trim().toLowerCase().startsWith('```mermaid')
                const isMermaid = language === 'mermaid' || isMermaidContent || isMermaidFence
                const jsxPreview = getJsxPreviewData(content, language)

                if (jsxPreview) {
                  return (
                    <GeneratedJSXPreview
                      bindings={jsxPreview.bindings}
                      isStreaming={!!isGenerating}
                      jsx={jsxPreview.jsx}
                    />
                  )
                }

                if (isOpenUIFenceLanguage(language)) {
                  return (
                    <OpenUIBlock
                      code={content}
                      isStreaming={!!isGenerating}
                      onActionPrompt={onInteractiveSubmit}
                    />
                  )
                }

                const htmlCard = renderHtmlCardFromFence(
                  content,
                  language,
                  `${messageId || 'msg'}-pre2-${language}`,
                  onInteractiveSubmit,
                  {
                    isStreaming: !!isGenerating,
                    states: htmlCardStates,
                    onStateChange: handleHtmlCardStateChange
                  }
                )
                if (htmlCard) return htmlCard

                if (isMermaid) {
                  safeConsole.log('MessageMarkdown: Found Mermaid content in pre')
                  let cleanContent = content
                  if (isMermaidFence) {
                    cleanContent = content
                      .replace(/^```mermaid\n?/, '')
                      .replace(/```$/, '')
                      .trim()
                  }
                  return (
                    <MermaidComponent
                      chatId={outcomeChatId}
                      content={cleanContent}
                      isStreaming={!!isGenerating}
                      messageId={outcomeMessageId}
                      shouldTrackOutcome={shouldTrackChatOutputOutcome}
                    />
                  )
                }
                return (
                  <pre className={className} {...props}>
                    {children}
                  </pre>
                )
              },
              code({
                className,
                children,
                ...props
              }: ComponentPropsWithoutRef<'code'> & { 'data-inline'?: boolean }) {
                const match = /language-([^\s]+)/.exec(className || '')
                const language = match ? match[1].toLowerCase() : ''
                const content = children?.toString() || ''
                const isMermaidContent = content
                  .trim()
                  .match(
                    /^(graph|flowchart|sequenceDiagram|classDiagram|stateDiagram|erDiagram|journey|gantt|pie|mindmap|timeline|gitGraph)\s/i
                  )
                const isMermaidFence = content.trim().toLowerCase().startsWith('```mermaid')
                const isMermaid = language === 'mermaid' || isMermaidContent || isMermaidFence

                if (!props['data-inline'] && isMermaid) {
                  safeConsole.log('MessageMarkdown: Skipping code block for Mermaid content')
                  return null // The diagram will be handled by the pre component
                }
                return (
                  <code className={className} {...props}>
                    {children}
                  </code>
                )
              }
            }}
          >
            {segment}
          </MessageMarkdownMemoized>
        )
      }
    })

    // Return the mixed content array as a fragment
    return <React.Fragment key={`part-fragment-${partIndex}`}>{mixedContent}</React.Fragment>
  })

  // Process remaining interactive components
  // Extract citation network, stakeholder map, scenario planner, and market positioner data
  const citationNetworkRegex = /\[CITATION_NETWORK\](.*?)\[\/CITATION_NETWORK\]/gs
  const citationNetworkData = citationNetworkRegex.exec(content)?.[1]
  const citationNetworkJson = citationNetworkData
    ? parseSafely<CitationNetworkData>(citationNetworkData.trim())
    : null

  const stakeholderMapRegex = /\[STAKEHOLDER_MAP\](.*?)\[\/STAKEHOLDER_MAP\]/gs
  const stakeholderMapData = stakeholderMapRegex.exec(content)?.[1]
  const stakeholderMapJson = stakeholderMapData
    ? parseSafely<StakeholderMapData>(stakeholderMapData.trim())
    : null

  const scenarioPlannerRegex = /\[SCENARIO_PLANNER\](.*?)\[\/SCENARIO_PLANNER\]/gs
  const scenarioPlannerData = scenarioPlannerRegex.exec(content)?.[1]
  const scenarioPlannerJson = scenarioPlannerData
    ? parseSafely<ScenarioPlannerData>(scenarioPlannerData.trim())
    : null

  const marketPositionerRegex = /\[MARKET_POSITIONER\](.*?)\[\/MARKET_POSITIONER\]/gs
  const marketPositionerData = marketPositionerRegex.exec(content)?.[1]
  const marketPositionerJson = marketPositionerData
    ? parseSafely<MarketPositionerData>(marketPositionerData.trim())
    : null

  return (
    <>
      {quoteMatch && (
        <AnimatePresence>
          <motion.div
            initial={{ opacity: 0, scale: 0.98, y: -5 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.98, y: -5 }}
            transition={{
              type: 'spring',
              stiffness: 400,
              damping: 25,
              mass: 0.6,
              duration: 0.4
            }}
            className="my-2 flex items-start gap-2"
          >
            <button
              type="button"
              onClick={() => {
                const quotedMessageId = quoteMatch[1]?.trim()
                const quotedText = quoteMatch[2]?.trim()
                if (chatId && quotedMessageId) {
                  try {
                    sessionStorage.setItem(
                      'highlight-quote',
                      JSON.stringify({ messageId: quotedMessageId, text: quotedText || '' })
                    )
                  } catch {
                    // ignore storage errors
                  }
                  const targetUrl = `/chat/${chatId}?highlight=${quotedMessageId}`
                  const isSameChat = pathname === `/chat/${chatId}`
                  if (isSameChat) {
                    router.replace(targetUrl)
                  } else {
                    navigateToSupportedHref(targetUrl)
                  }
                }
              }}
              disabled={!chatId || !quoteMatch[1]?.trim()}
              className={cn(
                'flex-1 rounded-lg bg-slate-100/80 p-3 text-left text-sm text-muted-foreground backdrop-blur-sm transition-colors dark:bg-neutral-800/80',
                chatId &&
                  quoteMatch[1]?.trim() &&
                  'cursor-pointer hover:bg-slate-200/80 dark:hover:bg-neutral-700/80'
              )}
              title={chatId && quoteMatch[1]?.trim() ? 'Scroll to source message' : undefined}
            >
              <div className="mb-2 flex items-center gap-2 text-xs opacity-70">
                <IconMessageReply size={14} className="shrink-0" />
                <span>Replying to</span>
              </div>
              <p className="line-clamp-3 whitespace-pre-wrap text-xs italic text-gray-600 dark:text-gray-400">
                {quoteMatch[2]}
              </p>
            </button>
          </motion.div>
        </AnimatePresence>
      )}

      <div
        ref={messageSelectionRootRef}
        onMouseUp={handleTextSelection}
        onTouchEnd={handleTextSelection}
        {...{ [MESSAGE_TEXT_SELECTION_ATTRIBUTE]: 'true' }}
        data-message-id={messageId}
        className="w-full select-text"
      >
        <div
          ref={proseContainerRef}
          className="prose-selection:bg-primary/20 prose-selection:text-foreground"
        >
          {renderedElements}
        </div>
      </div>
      {selectedText && selectionActionPosition && (
        <SelectionActionBubbles
          copied={selectionCopied}
          position={selectionActionPosition}
          onCopy={() => void handleCopySelection()}
          onReply={handleReplyToSelection}
          onHighlight={handleHighlightSelection}
          onDismiss={clearSelectedText}
        />
      )}
      {highlights.length > 0 && (
        <SavedMessageHighlights
          containerRef={proseContainerRef}
          contentSignature={rawContent}
          highlights={highlights}
        />
      )}
      {highlightQuoteText && (
        <HighlightQuotedText containerRef={proseContainerRef} text={highlightQuoteText} />
      )}

      {/* Render complex special components that weren't handled inline - expandable to modal */}
      {citationNetworkJson && (
        <ExpandableInteractiveWrapper title="Citation Network">
          <CitationNetwork data={citationNetworkJson} />
        </ExpandableInteractiveWrapper>
      )}
      {stakeholderMapJson && (
        <ExpandableInteractiveWrapper title="Stakeholder Map">
          <StakeholderMap data={stakeholderMapJson} />
        </ExpandableInteractiveWrapper>
      )}
      {scenarioPlannerJson && (
        <ExpandableInteractiveWrapper title="Scenario Planner">
          <ScenarioPlanner data={scenarioPlannerJson} />
        </ExpandableInteractiveWrapper>
      )}
      {marketPositionerJson && (
        <ExpandableInteractiveWrapper title="Market Positioner">
          <MarketPositioner data={marketPositionerJson} />
        </ExpandableInteractiveWrapper>
      )}
    </>
  )
}
